import _ from 'lodash'
import React, { useMemo, useState, useEffect } from 'react'
import { useSelector } from 'react-redux'
import { StyleElement } from '../../Main/StyleElement'
import { isImmutable, List, Map, hash, fromJS } from 'immutable'
import { useLayoutMode } from '../../../hooks/useLayoutMode'
import { getThemeFormFieldsKeySet } from '../../../constants/getThemeFormFieldsKeySet'
import { alignToFlex, toJS } from '../../../lib/common'
import { THEME_FORM_FIELD_ELEMENTS_KEYS } from '../../../constants/THEME_FORM_FIELD_ELEMENTS_KEYS'
import { isHex } from '../../../lib/themeutils'
import { getSizeProp, getStyle } from '../../../constants/getStyle'
import { isSame } from './isSame'
import { transformCSSPositionValue } from '../../../hooks/useWidgetComponent'
import { getScrollBarStyles, kebabCase } from '../../../constants/addStyleClasses'
import { deepClean, mergeDeep } from '../../../lib/mergeDeep'
import { ALL_SIZE_KEYS } from '../../../constants/CardComponent'
import { SURVEY_EMAIL_TYPE, SURVEY_TYPE } from '../../../constants/features'
import { LAYOUT_MODE } from '../../../constants/toUnits'

const errorsFieldsControls = [
  'Search',
  'AddressFinder',
  'ExternalLookup',
  'Range',
  'StarRating',
  'Ranking',
  'Rating',
  'YesNo',
  'Matrix',
  'CES',
  'ImageChoice',
  'SurveyList',
]

export const mirrorPosition = (pos) => {
  if (pos == 'row') {
    return 'row-reverse'
  } else if (pos === 'row-reverse') {
    return 'row'
  }
  return pos
}

export const renderShadowCss = (style) => {
  if (style?.shadow) {
    return `box-shadow: ${getStyle(style).boxShadow}`
  }
  return ''
}

export const getStyleObjCss = (styles, important = '') =>
  _.reduce(styles, (r, v, k) => [...r, `${kebabCase(k)}: ${trimFont(v)}${important};`], []).join('')

const trimFont = (font) => {
  return _.trim(font, ';')
}

const toCSS = (className, body, ...values) => {
  let rules = _.compact(_.map(values, (v) => Boolean(v)))
  if (rules.length === 0) {
    return ''
  } else {
    return `${className}{${body}}`
  }
}

const fixCSSNumInHave = (num) => {
  if (num === '') {
    return num
  } else if (String(num).indexOf('px') !== -1) {
    return num
  } else if (/([0-9\.]*em|rem|ww|wh)$/.test(num)) {
    return num
  } else {
    let nextNum = parseInt(num, 10)
    nextNum = _.isNaN(nextNum) ? 0 : nextNum
    return `${nextNum}px`
  }
}

export const renderBorderStyle = (style, path = [], key = '', def = '', isImportant = false) => {
  let str = ''

  if (!key && _.get(style, [...path, '--ft-border'])) {
    str += renderBorderStyle(style, path, 'Top', def, isImportant)
    str += renderBorderStyle(style, path, 'Left', def, isImportant)
    str += renderBorderStyle(style, path, 'Right', def, isImportant)
    str += renderBorderStyle(style, path, 'Bottom', def, isImportant)
  } else {
    let color = _.get(style, [...path, `border${key}Color`], def)
    if (color) {
      str += `border-${String(key ? `${key}-` : '').toLowerCase()}color: ${color} ${isImportant ? '!important' : ''};`
    }
    let styleVal = _.get(style, [...path, `border${key}Style`], def)
    if (styleVal) {
      str += `border-${String(key ? `${key}-` : '').toLowerCase()}style: ${styleVal}${isImportant ? '!important' : ''};`
    }
    let width = _.get(style, [...path, `border${key}Width`], def)
    if (width) {
      if (_.endsWith(width, 'px')) {
        width = String(width).replace('px', '')
      }
      str += `border-${String(key ? `${key}-` : '').toLowerCase()}width: ${transformCSSPositionValue(width, 'px', true)}${isImportant ? '!important' : ''};`
    }
  }

  return str
}

const renderMarginStyle = (style, isImportant = false) => {
  let marginStyle = ''
  let important = isImportant ? '!important' : ''

  let left = _.get(style, ['marginLeft'], '')
  let top = _.get(style, ['marginTop'], '')
  let right = _.get(style, ['marginRight'], '')
  let bottom = _.get(style, ['marginBottom'], '')
  if (left !== '') {
    marginStyle += `margin-left: ${transformCSSPositionValue(_.get(style, 'marginLeft', ''), _.get(style, 'marginLeftUnit', 'px'), true)}${important};`
  }
  if (top !== '') {
    marginStyle += `margin-top: ${transformCSSPositionValue(_.get(style, 'marginTop', ''), _.get(style, 'marginTopUnit', 'px'), true)}${important};`
  }
  if (right !== '') {
    marginStyle += `margin-right: ${transformCSSPositionValue(_.get(style, 'marginRight', ''), _.get(style, 'marginRightUnit', 'px'), true)}${important};`
  }
  if (bottom !== '') {
    marginStyle += `margin-bottom: ${transformCSSPositionValue(_.get(style, 'marginBottom', ''), _.get(style, 'marginBottomUnit', 'px'), true)}${important};`
  }

  return marginStyle
}

const SHRINK_VARIANT = ['standard', 'filled', 'outlined', 'classic']

const renderMUILabelFieldStyle = (fields, typeStyle, defaultStyle, className, allowMargin = false) => {
  if (defaultStyle?.labelShrink?.labelShrink) {
    defaultStyle = mergeDeep({}, defaultStyle, defaultStyle?.labelShrink, defaultStyle?.labelShrink?.labelShrink)
  }
  const style = mergeDeep({}, _.pickBy(defaultStyle, _.identity), _.pickBy(typeStyle, _.identity))
  let defaultShrinkColor = _.get(
    style,
    'labelShrink.labelColorShrink',
    _.get(style, 'primary', className ? '' : _.get(style, 'labelColor', ''))
  )
  let marginStyle = renderMarginStyle(_.get(style, 'label', {}))
  let marginShrinkStyle = renderMarginStyle(_.get(style, 'labelShrink', {}))
  let marginFocusedStyle = renderMarginStyle(_.get(style, 'labelFocused', {}), true)
  let marginLabelShrinkFocusedStyle = renderMarginStyle(_.get(style, 'labelShrinkFocused', {}), true)

  let shrinkClasses = (className) =>
    _.map(SHRINK_VARIANT, (variant) => `.ft--theme--FormField--${variant}${className}`).join(',')
  let shrinkSubClasses = (className, subClassName) =>
    _.map(SHRINK_VARIANT, (variant) => `.ft--theme--FormField--${variant}${className} ${subClassName}`).join(',')

  return `${toCSS(
    fields.map((v) => `${className}.ft--theme--FormField--${v} .ft-helper-txt`).join(','),
    ` color: ${_.get(style, 'error', '')};
            font-family: ${trimFont(_.get(style, ['errorMsg', 'fontFamily'], className ? '' : ''))};
            font-size: ${_.get(style, ['errorMsg', 'fontSize'], className ? '' : '14px')};
            font-weight: ${_.get(style, ['errorMsg', 'fontWeight'], className ? '' : 'normal')};
            padding-left: ${transformCSSPositionValue(_.get(style, ['errorMsg', 'paddingLeft'], ''), _.get(style, ['errorMsg', 'paddingLeftUnit'], 'px'), true)};
        padding-right: ${transformCSSPositionValue(_.get(style, ['errorMsg', 'paddingRight'], ''), _.get(style, ['errorMsg', 'paddingRightUnit'], 'px'), true)};
        padding-top: ${transformCSSPositionValue(_.get(style, ['errorMsg', 'paddingTop'], ''), _.get(style, ['errorMsg', 'paddingTopUnit'], 'px'), true)};
        padding-bottom: ${transformCSSPositionValue(_.get(style, ['errorMsg', 'paddingBottom'], ''), _.get(style, ['errorMsg', 'paddingBottomUnit'], 'px'), true)};`,
    _.get(style, 'error', ''),
    _.get(style, ['errorMsg', 'fontFamily'], className ? '' : ''),
    _.get(style, ['errorMsg', 'fontSize'], className ? '' : '14px'),
    _.get(style, ['errorMsg', 'fontWeight'], className ? '' : 'normal'),
    _.get(style, ['errorMsg', 'paddingLeft'], ''),
    _.get(style, ['errorMsg', 'paddingRight'], ''),
    _.get(style, ['errorMsg', 'paddingTop'], ''),
    _.get(style, ['errorMsg', 'paddingBottom'], '')
  )}
    ${
      fields.includes('phone')
        ? `${toCSS(
            fields.map((v) => `${className}.ft--theme--FormField--${v} .ft--label`).join(','),
            `transform: translate(54px, 20px) scale(1);`,
            !className
          )}${toCSS(
            fields
              .map((v) => `${className}.ft--theme--FormField--${v}.ft--theme--FormField--standard .ft--label`)
              .join(','),
            `transform: translate(44px, 20px) scale(1);`,
            !className
          )}${toCSS(
            fields
              .map(
                (v) => `${className}.ft--theme--FormField--${v}.ft--theme--FormField--standard .ft--label.ft--shrink`
              )
              .join(','),
            `transform: translate(${fixCSSNumInHave(_.get(style, ['leftOffset'], className ? '' : '0'))}, ${fixCSSNumInHave(_.get(style, ['topOffset'], className ? '' : ''))}) scale(1);`,
            !className
          )}${toCSS(
            fields
              .map((v) => `${className}.ft--theme--FormField--${v}.ft--theme--FormField--filled .ft--label`)
              .join(','),
            `transform: translate(54px, 28px) scale(1);`,
            !className
          )}${toCSS(
            fields
              .map((v) => `${className}.ft--theme--FormField--${v}.ft--theme--FormField--filled .ft--label.ft--shrink`)
              .join(','),
            `transform: translate(${fixCSSNumInHave(_.get(style, ['leftOffset'], className ? '' : '0'))}, ${fixCSSNumInHave(_.get(style, ['topOffset'], className ? '' : ''))}) scale(1);`,
            !className
          )}${toCSS(
            fields
              .map((v) => `${className}.ft--theme--FormField--${v}.ft--theme--FormField--fixed .ft--label`)
              .join(','),
            `transform: none;`,
            !className
          )}`
        : ``
    }
    ${toCSS(
      `${className}.ft--theme--FormField--label,` +
        fields.map((v) => `${className}.ft--theme--FormField--${v} .ft--label`).join(','),
      `color: ${_.get(style, 'labelColor', '')} !important;
font-family: ${trimFont(_.get(style, ['label', 'fontFamily'], className ? '' : ''))};
font-size: ${_.get(style, ['label', 'fontSize'], className ? '' : '12px')};
font-weight: ${_.get(style, ['label', 'fontWeight'], className ? '' : 'normal')};
line-height: ${_.get(style, ['label', 'lineHeight'], '')};
text-align: ${_.get(style, ['label', 'textAlign'], className ? '' : '')};${marginStyle}
${_.get(style, ['label', '--transition-duration'], '') ? `--transition-duration: ${_.get(style, ['label', '--transition-duration'], '')}ms;` : ''}${marginStyle}
`,
      _.get(style, 'labelColor', ''),
      _.get(style, ['label', 'fontFamily'], className ? '' : ''),
      _.get(style, ['label', 'fontSize'], className ? '' : '12px'),
      _.get(style, ['label', 'lineHeight'], ''),
      _.get(style, ['label', 'fontWeight'], className ? '' : 'normal'),
      _.get(style, ['label', 'textAlign'], className ? '' : ''),
      _.get(style, ['label', 'marginLeft'], ''),
      _.get(style, ['label', 'marginTop'], ''),
      _.get(style, ['label', 'marginRight'], ''),
      _.get(style, ['label', 'marginBottom'], ''),
      _.get(style, ['label', '--transition-duration'], '')
    )}
    ${toCSS(
      `.ft--theme--FormField--fixed ${className}.ft--theme--FormField--label.ftFocusIn,.ft--theme--FormField--fixed ${className}.ft--theme--FormField--label.Mui-focused:not(.ftNotFocusIn),` +
        fields
          .map(
            (v) =>
              `${className}.ft--theme--FormField--${v}.ft--theme--FormField--fixed .ft--label.Mui-focused:not(.ftNotFocusIn), ${className}.ft--theme--FormField--fixed.ft--theme--FormField--${v} .ft--label.ftFocusIn`
          )
          .join(','),
      `color: ${_.get(style, 'labelFocusedColor', '')} !important;
                font-family: ${trimFont(_.get(style, ['labelFocused', 'fontFamily'], ''))}!important;
        font-size: ${_.get(style, ['labelFocused', 'fontSize'], '')}!important;
        font-weight: ${_.get(style, ['labelFocused', 'fontWeight'], '')}!important;
        line-height: ${_.get(style, ['labelFocused', 'lineHeight'], '')}!important;
        text-align: ${_.get(style, ['labelFocused', 'textAlign'], '')}!important;${marginFocusedStyle}`,
      _.get(style, 'labelFocusedColor', ''),
      _.get(style, ['labelFocused', 'fontFamily'], ''),
      _.get(style, ['labelFocused', 'fontSize'], ''),
      _.get(style, ['labelFocused', 'lineHeight'], ''),
      _.get(style, ['labelFocused', 'fontWeight'], ''),
      _.get(style, ['labelFocused', 'textAlign'], ''),
      _.get(style, ['labelFocused', 'marginLeft'], ''),
      _.get(style, ['labelFocused', 'marginTop'], ''),
      _.get(style, ['labelFocused', 'marginRight'], ''),
      _.get(style, ['labelFocused', 'marginBottom'], '')
    )}
    ${
      fields.includes('checkbox') ||
      fields.includes('radio') ||
      fields.includes('switch') ||
      fields.includes('switcher')
        ? `${toCSS(
            `${className}.ft--theme--FormField--label,` +
              fields.map((v) => `${className}.ft--theme--FormField--${v} .ft--label.ft--shrink`).join(','),
            `transform: none;`,
            true
          )}`
        : `${toCSS(
            `${className}.ft--theme--FormField--label,` +
              fields.map((v) => `${className}.ft--theme--FormField--${v} .ft--label.ft--shrink`).join(','),
            `transform: translate(${fixCSSNumInHave(_.get(style, ['leftOffset'], className ? '' : '0'))}, ${fixCSSNumInHave(_.get(style, ['topOffset'], className ? '' : ''))}) scale(1);`,
            true
          )}
    ${toCSS(
      `.ft--rtl ${className}.ft--theme--FormField--label,` +
        fields.map((v) => `.ft--rtl ${className}.ft--theme--FormField--${v} .ft--label.ft--shrink`).join(','),
      `transform: translate(-${fixCSSNumInHave(_.get(style, ['leftOffset'], className ? '' : '0'))}, ${fixCSSNumInHave(_.get(style, ['topOffset'], className ? '' : ''))}) scale(1);`,
      true
    )}
    ${toCSS(
      `[dir="rtl"] .ft--theme--FormField--outlined ${className}.ft--theme--FormField--label,` +
        fields
          .map(
            (v) =>
              `[dir="rtl"] .ft--theme--FormField--outlined ${className}.ft--theme--FormField--${v} .ft--label.ft--shrink`
          )
          .join(','),
      `transform: translate(calc(-${fixCSSNumInHave(_.get(style, ['leftOffset'], className ? '' : '0'))} - 6px), ${fixCSSNumInHave(_.get(style, ['topOffset'], className ? '' : ''))}) scale(1);`,
      true
    )}
    ${toCSS(
      `${className}.ft--theme--FormField--label,` +
        fields
          .map((v) => `${className}.ft--theme--FormField--${v}.ft--theme--FormField--standard .ft--label.ft--shrink`)
          .join(','),
      `transform: translate(${fixCSSNumInHave(_.get(style, ['leftOffset'], className ? '' : '0'))}, ${fixCSSNumInHave(_.get(style, ['topOffset'], className ? '' : '0px'))}) scale(1);`,
      true
    )}
    ${toCSS(
      `${className}.ft--theme--FormField--label,` +
        fields
          .map((v) => `${className}.ft--theme--FormField--${v}.ft--theme--FormField--filled .ft--label.ft--shrink`)
          .join(','),
      `transform: translate(${fixCSSNumInHave(_.get(style, ['leftOffset'], className ? '' : '12px'))}, ${fixCSSNumInHave(_.get(style, ['topOffset'], className ? '' : '0px'))}) scale(1);`,
      true
    )}
    ${toCSS(
      `[dir="rtl"] ${className}.ft--theme--FormField--label,` +
        fields
          .map(
            (v) =>
              `[dir="rtl"] ${className}.ft--theme--FormField--${v}.ft--theme--FormField--filled .ft--label.ft--shrink`
          )
          .join(','),
      `transform: translate(${fixCSSNumInHave(_.get(style, ['leftOffset'], className ? '' : '-12px'))}, ${fixCSSNumInHave(_.get(style, ['topOffset'], className ? '' : '0px'))}) scale(1);`,
      true
    )}
    ${toCSS(
      `${className}.ft--theme--FormField--label,` +
        fields
          .map((v) => `${className}.ft--theme--FormField--${v}.ft--theme--FormField--outlined .ft--label.ft--shrink`)
          .join(','),
      `transform: translate(${fixCSSNumInHave(parseInt(_.get(style, ['leftOffset'], className ? 0 : 8), 10) + 6)}, ${fixCSSNumInHave(_.get(style, ['topOffset'], className ? '' : '0px'))}) scale(1);`,
      true
    )}`
    }
    ${toCSS(
      `${className}.ft--theme--FormField--label,` +
        fields.map((v) => `${className}.ft--theme--FormField--${v} .ft--label.ft--shrink`).join(','),
      `color: ${_.get(style, 'labelColorShrink', defaultShrinkColor)} !important;
font-family: ${trimFont(_.get(style, ['labelShrink', 'fontFamily'], _.get(style, ['label', 'fontFamily'], className ? '' : '')))};
font-size: ${_.get(style, ['labelShrink', 'fontSize'], _.get(style, ['label', 'fontSize'], className ? '' : '12px'))};
line-height: ${_.get(style, ['labelShrink', 'lineHeight'], _.get(style, ['label', 'lineHeight'], ''))};
font-weight: ${_.get(style, ['labelShrink', 'fontWeight'], _.get(style, ['label', 'fontWeight'], className ? '' : 'normal'))};${marginStyle}${marginShrinkStyle}
${_.get(style, ['labelShrink', '--transition-duration'], '') ? `--transition-duration: ${_.get(style, ['labelShrink', '--transition-duration'], '')}ms;` : ''}`,
      _.get(style, 'labelColorShrink', defaultShrinkColor),
      _.get(style, ['labelShrink', 'fontFamily'], _.get(style, ['label', 'fontFamily'], className ? '' : '')),
      _.get(style, ['labelShrink', 'fontSize'], _.get(style, ['label', 'fontSize'], className ? '' : '12px')),
      _.get(style, ['labelShrink', 'fontWeight'], _.get(style, ['label', 'fontWeight'], className ? '' : 'normal')),
      _.get(style, ['labelShrink', 'lineHeight'], _.get(style, ['label', 'lineHeight'], '')),
      marginStyle,
      marginShrinkStyle
    )}
            ${toCSS(
              `${shrinkClasses(`${className}.ft--theme--FormField--label.ftFocusIn`)},${shrinkClasses(`${className}.ft--theme--FormField--label.Mui-focused:not(.ftNotFocusIn)`)},` +
                fields
                  .map(
                    (
                      v
                    ) => `${shrinkSubClasses(`${className}.ft--theme--FormField--${v}`, '.ft--label.ft--shrink.Mui-focused:not(.ftNotFocusIn)')},
                    ${shrinkSubClasses(`${className}.ft--theme--FormField--${v}`, '.ft--label.ft--shrink.ftFocusIn')}`
                  )
                  .join(','),
              `color: ${_.get(style, ['labelShrinkFocused', 'color'], '')} !important;
                font-family: ${trimFont(_.get(style, ['labelShrinkFocused', 'fontFamily'], ''))}!important;
                font-size: ${_.get(style, ['labelShrinkFocused', 'fontSize'], '')}!important;
                font-weight: ${_.get(style, ['labelShrinkFocused', 'fontWeight'], '')}!important;
                line-height: ${_.get(style, ['labelShrinkFocused', 'lineHeight'], '')}!important;
                text-align: ${_.get(style, ['labelShrinkFocused', 'textAlign'], '')}!important;${marginLabelShrinkFocusedStyle}`,
              _.get(style, ['labelShrinkFocused', 'color'], ''),
              _.get(style, ['labelShrinkFocused', 'fontFamily'], ''),
              _.get(style, ['labelShrinkFocused', 'fontSize'], ''),
              _.get(style, ['labelShrinkFocused', 'lineHeight'], ''),
              _.get(style, ['labelShrinkFocused', 'fontWeight'], ''),
              _.get(style, ['labelShrinkFocused', 'textAlign'], ''),
              _.get(style, ['labelShrinkFocused', 'marginLeft'], ''),
              _.get(style, ['labelShrinkFocused', 'marginTop'], ''),
              _.get(style, ['labelShrinkFocused', 'marginRight'], ''),
              _.get(style, ['labelShrinkFocused', 'marginBottom'], '')
            )}
    ${toCSS(
      `${className}.ft--theme--FormField--asterix,` +
        fields.map((v) => `${className}.ft--theme--FormField--${v} .ft--theme--FormField--asterix`).join(','),
      `color: ${_.get(style, 'colorAsterix')} !important;
        font-size: ${_.get(style, ['asterix', 'fontSize'], 12)};
        font-weight: ${_.get(style, ['asterix', 'fontWeight'])};`,
      _.get(style, 'colorAsterix'),
      _.get(style, ['asterix', 'fontSize']),
      _.get(style, ['asterix', 'fontWeight'])
    )}
    ${toCSS(
      `${className}.ft--theme--FormField--label,` +
        fields.map((v) => `${className}.ft--theme--FormField--${v} legend`).join(','),
      `color: ${_.get(style, 'labelColorShrink', _.get(style, 'labelColor', ''))} !important;
font-family: ${trimFont(_.get(style, ['labelShrink', 'fontFamily'], _.get(style, ['label', 'fontFamily'], className ? '' : '')))};
font-size: ${_.get(style, ['labelShrink', 'fontSize'], _.get(style, ['label', 'fontSize'], className ? '' : '12px'))};
font-weight: ${_.get(style, ['labelShrink', 'fontWeight'], _.get(style, ['label', 'fontWeight'], className ? '' : 'normal'))};`,
      _.get(style, 'labelColorShrink', _.get(style, 'labelColor', '')),
      _.get(style, ['labelShrink', 'fontFamily'], _.get(style, ['label', 'fontFamily'], className ? '' : '')),
      _.get(style, ['labelShrink', 'fontSize'], _.get(style, ['label', 'fontSize'], className ? '' : '12px')),
      _.get(style, ['labelShrink', 'fontWeight'], _.get(style, ['label', 'fontWeight'], className ? '' : 'normal'))
    )}
    ${toCSS(
      fields.map((v) => `${className}.ft--theme--FormField--${v}.ftsuccess .ft-helper-txt`).join(','),
      `color: ${_.get(style, 'success', '')}!important;`,
      _.get(style, 'success', '')
    )}`
}

const renderMUIPrefixStyle = (fields, typeStyle, defaultStyle, className) => {
  const style = mergeDeep({}, _.pickBy(defaultStyle, _.identity), _.pickBy(typeStyle, _.identity))

  if (!_.isArray(fields)) {
    return ''
  }

  return `${toCSS(
    fields.map((v) => `${className}.ft--theme--FormField--${v} .ft--theme--FormField--startTxt`).join(','),
    ` color: ${_.get(style, 'prefix.color', '')};
            font-family: ${trimFont(_.get(style, ['prefix', 'fontFamily'], className ? '' : 'cursive'))};
            font-size: ${_.get(style, ['prefix', 'fontSize'], className ? '' : '14px')};
            font-weight: ${_.get(style, ['prefix', 'fontWeight'], className ? '' : 'normal')};`,
    _.get(style, 'prefix.color', ''),
    _.get(style, ['prefix', 'fontFamily'], className ? '' : 'cursive'),
    _.get(style, ['prefix', 'fontSize'], className ? '' : '14px'),
    _.get(style, ['prefix', 'fontWeight'], className ? '' : 'normal')
  )}
    ${toCSS(
      fields.map((v) => `${className}.ft--theme--FormField--${v} .ft--theme--FormField--endTxt`).join(','),
      ` color: ${_.get(style, 'suffix.color', '')};
            font-family: ${trimFont(_.get(style, ['suffix', 'fontFamily'], className ? '' : 'cursive'))};
            font-size: ${_.get(style, ['suffix', 'fontSize'], className ? '' : '14px')};
            font-weight: ${_.get(style, ['suffix', 'fontWeight'], className ? '' : 'normal')};`,
      _.get(style, 'suffix.color', ''),
      _.get(style, ['suffix', 'fontFamily'], className ? '' : 'cursive'),
      _.get(style, ['suffix', 'fontSize'], className ? '' : '14px'),
      _.get(style, ['suffix', 'fontWeight'], className ? '' : 'normal')
    )}
    ${toCSS(
      fields.map((v) => `${className}.ft--theme--FormField--${v} .ft--theme--FormField--startIcon`).join(','),
      `color: ${_.get(style, 'prefixIcon.color', '')} !important;
        font-size: ${_.get(style, ['prefixIcon', 'fontSize'], className ? '' : '24px')} !important;`,
      _.get(style, 'prefixIcon.color', ''),
      _.get(style, ['prefixIcon', 'fontSize'], className ? '' : '24px')
    )}
    ${toCSS(
      fields.map((v) => `${className}.ft--theme--FormField--${v} .ft--theme--FormField--endIcon`).join(','),
      `color: ${_.get(style, 'suffixIcon.color', '')} !important;
        font-size: ${_.get(style, ['suffixIcon', 'fontSize'], className ? '' : '24px')}!important;`,
      _.get(style, 'suffixIcon.color', ''),
      _.get(style, ['suffixIcon', 'fontSize'], className ? '' : '24px')
    )}`
}

const renderMUISignatureStyle = (checkFields, typeStyle, defaultStyle, className) => {
  const style = mergeDeep({}, _.pickBy(defaultStyle, _.identity), _.pickBy(typeStyle, _.identity))
  let extra = ''
  if (_.size(style.trashIcon)) {
    const styles = getStyle(style.trashIcon)
    extra += `${toCSS(`${className}.ft--theme--FormField--signature .trash-icon`, getStyleObjCss(styles), true)}`
  }

  return `${renderMUITextFieldStyle(checkFields, typeStyle, defaultStyle, className)}${extra}`
}
const renderMUIUploadFieldStyle = (checkFields, typeStyle, defaultStyle, className) => {
  const iconPosition = {
    bottom: `". . ."
        ". label ."
        ". icon ."`,
    left: `". . ."
        "icon label ."
        ". . ."`,
    top: `". icon ."
        ". label ."
        ". . ."`,
    right: `". . ."
        ". label icon"
        ". . ."`,
  }
  let extra = ''
  const style = mergeDeep({}, _.pickBy(defaultStyle, _.identity), _.pickBy(typeStyle, _.identity))
  let fileUploadVariant = _.get(style, 'fileUploadVariant', className ? '' : 'dropzone')
  const isFieldOnly = fileUploadVariant === 'button'
  let iconVariant = _.get(style, 'iconVariant', className ? '' : 'bottom')
  iconVariant = iconVariant ? (_.keys(iconPosition).includes(iconVariant) ? iconVariant : 'bottom') : ''
  let labelWidth = _.get(style, 'labelWidth', className ? '' : '')
    ? `${checkFields.map((v) => `${className}.ft--theme--FormField--${v} .ft--label`).join(',')}{
        width: ${transformCSSPositionValue(_.get(style, 'labelWidth', className ? '' : ''), 'px')};
    }`
    : ''
  let width = transformCSSPositionValue(
    _.get(style, 'upload.width', _.get(style, 'root.width', '')),
    _.get(style, 'upload.widthUnit', _.get(style, 'root.widthUnit', 'px')),
    true
  )
  let height = transformCSSPositionValue(
    _.get(style, 'upload.height', _.get(style, 'root.height', '')),
    _.get(style, 'upload.heightUnit', _.get(style, 'root.heightUnit', 'px')),
    true
  )
  let minHeight = transformCSSPositionValue(
    _.get(style, 'upload.minHeight', _.get(style, 'root.minHeight', '')),
    _.get(style, 'upload.minHeightUnit', _.get(style, 'root.minHeightUnit', 'px')),
    true
  )
  let cssWidth = ''
  if (width) {
    cssWidth = `${toCSS(`${checkFields.map((v) => `${className}.ft--theme--FormField--${v}.ft--theme--FormField-container`).join(',')}`, `width: ${width};`, width)}`
  }
  let cssHeight = ''
  if (height) {
    cssHeight = `${toCSS(`${className}.ft--theme--FormField--fileupload .ft--FormField--dropzone`, `height: ${height};`, height)}`
    cssHeight += `${toCSS(`${className}.ft--theme--FormField--fileupload.ft--theme--FormField--button`, `height: ${height};`, height)}`
  }
  if (minHeight) {
    cssHeight += `${toCSS(`${className}.ft--theme--FormField--fileupload .ft--FormField--dropzone`, `min-height: ${minHeight};`, minHeight)}`
  }
  if (style.dropdownItemFill || _.size(_.get(style, 'item', {})) > 0) {
    let paperClass = className
    if (paperClass) {
      paperClass += '--option'
    }
    extra += toCSS(
      `${paperClass}.ft--theme--FormField--fileupload--option`,
      `background-color: ${_.get(style, 'dropdownItemFill', className ? '' : '')} !important;
            color: ${_.get(style, 'textItem', className ? '' : '')};
            font-size: ${_.get(style, 'inputItem.fontSize', _.get(style, 'fontSize', ''))};
            font-family: ${_.get(style, 'inputItem.fontFamily', '')};
            font-weight: ${_.get(style, 'inputItem.fontWeight', '')};
            border-width: ${transformCSSPositionValue(_.get(style, 'item.borderWidth', className ? '' : ''), _.get(style, 'item.borderWidthUnit', 'px'))};
            border-style: ${_.get(style, 'item.borderStyle', className ? '' : '')};
            border-color: ${_.get(style, 'item.borderColor', className ? '' : '')};
            padding-left: ${transformCSSPositionValue(_.get(style, 'item.paddingLeft', ''), _.get(style, 'item.paddingLeftUnit', 'px'), true)};
        padding-right: ${transformCSSPositionValue(_.get(style, 'item.paddingRight', ''), _.get(style, 'item.paddingRightUnit', 'px'), true)};
        padding-top: ${transformCSSPositionValue(_.get(style, 'item.paddingTop', ''), _.get(style, 'item.paddingTopUnit', 'px'), true)};
        padding-bottom: ${transformCSSPositionValue(_.get(style, 'item.paddingBottom', ''), _.get(style, 'item.paddingBottomUnit', 'px'), true)};max-width: 100%;`,
      _.get(style, 'dropdownItemFill', className ? '' : ''),
      _.get(style, 'textItem', ''),
      _.get(style, 'inputItem.fontFamily', ''),
      _.get(style, 'inputItem.fontSize', ''),
      _.get(style, 'inputItem.fontWeight', ''),
      _.get(style, 'item.borderWidth', ''),
      _.get(style, 'item.borderStyle', ''),
      _.get(style, 'item.borderColor', ''),
      _.get(style, 'item.paddingLeft', ''),
      _.get(style, 'item.paddingLeftUnit', 'px'),
      _.get(style, 'item.paddingRight', ''),
      _.get(style, 'item.paddingRightUnit', 'px'),
      _.get(style, 'item.paddingTop', ''),
      _.get(style, 'item.paddingTopUnit', 'px'),
      _.get(style, 'item.paddingBottom', ''),
      _.get(style, 'item.paddingBottomUnit', 'px')
    )
  }
  if (style.dropdownListFill || _.size(style.menu) > 0) {
    let styles = getStyle(style.menu)
    let paperClass = className
    if (paperClass) {
      paperClass += '--menu'
    }
    let boxShadow = styles.boxShadow
    let borderRadius = _.get(style, 'menu.borderRadius', '')
    if (/^[0-9]+$/.test(borderRadius)) {
      borderRadius = `${borderRadius}px`
    }
    extra += toCSS(
      `${paperClass}.ft--theme--FormField--fileupload--menu`,
      `background-color: ${_.get(style, 'dropdownListFill', className ? '' : '')} !important;
            border-width: ${transformCSSPositionValue(_.get(style, 'menu.borderWidth', className ? '' : ''), _.get(style, 'menu.borderWidthUnit', 'px'))};
            border-style: ${_.get(style, 'menu.borderStyle', className ? '' : '')};
            border-color: ${_.get(style, 'menu.borderColor', className ? '' : '')};
            padding-left: ${transformCSSPositionValue(_.get(style, 'menu.paddingLeft', ''), _.get(style, 'menu.paddingLeftUnit', 'px'), true)};
            padding-right: ${transformCSSPositionValue(_.get(style, 'menu.paddingRight', ''), _.get(style, 'menu.paddingRightUnit', 'px'), true)};
            padding-top: ${transformCSSPositionValue(_.get(style, 'menu.paddingTop', ''), _.get(style, 'menu.paddingTopUnit', 'px'), true)};
            padding-bottom: ${transformCSSPositionValue(_.get(style, 'menu.paddingBottom', ''), _.get(style, 'menu.paddingBottomUnit', 'px'), true)};
            border-radius: ${borderRadius};box-shadow: ${boxShadow};`,
      _.get(style, 'dropdownListFill', className ? '' : ''),
      _.get(style, 'menu.borderWidth', ''),
      _.get(style, 'menu.borderStyle', ''),
      _.get(style, 'menu.borderColor', ''),
      _.get(style, 'menu.paddingLeft', ''),
      _.get(style, 'menu.paddingLeftUnit', 'px'),
      _.get(style, 'menu.paddingRight', ''),
      _.get(style, 'menu.paddingRightUnit', 'px'),
      _.get(style, 'menu.paddingTop', ''),
      _.get(style, 'menu.paddingTopUnit', 'px'),
      _.get(style, 'menu.paddingBottom', ''),
      _.get(style, 'menu.paddingBottomUnit', 'px'),
      borderRadius,
      boxShadow
    )
  }
  if (style.scroll) {
    let paperClass = className
    if (paperClass) {
      paperClass += '--menu'
    }
    let styleObject = getStyle(style.scroll)
    const scrollBarColor = _.get(styleObject, 'scrollBarColor', '')
    const scrollBarImage = _.get(styleObject, 'scrollBarImage', '')
    const thumbColor = _.get(styleObject, 'thumbColor', '')
    const thumbImage = _.get(styleObject, 'thumbImage', '')
    const borderRadius = _.get(styleObject, 'borderRadius', '')
    let borderWidth = _.get(styleObject, 'borderWidth', '')
    let borderColor = _.get(styleObject, 'borderColor', '')
    let borderStyle = _.get(styleObject, 'borderStyle', '')
    const isCustomScroll =
      (Boolean(scrollBarColor) || Boolean(scrollBarImage)) && (Boolean(thumbColor) || Boolean(thumbImage))
    let classBody = _.reduce(styleObject, (r, v, k) => [...r, `${kebabCase(k)}: ${v};`], []).join('')
    if (isCustomScroll) {
      classBody = 'scrollbar-width: thin;'
      if (scrollBarColor && thumbColor) {
        classBody += `scrollbar-color: ${thumbColor} ${scrollBarColor};`
      }
      const classes = []
      let height = _.get(styleObject, 'height')
      let heightUnit = _.get(styleObject, 'heightUnit', 'px')
      if (!height) {
        height = _.get(styleObject, '--scroll-height')
        heightUnit = _.get(styleObject, '--scroll-heightUnit', 'px')
      }
      if (height && height !== 'auto' && height !== 'unset') {
        height = _.endsWith(height, 'px') ? String(height).replace('px', '') : height
        height = transformCSSPositionValue(height, heightUnit, true)
        height = `height:${height} !important;`
      } else {
        height = ''
      }
      classes.push(`${paperClass}.ft--theme--FormField--fileupload--menu{${classBody}}`)
      classes.push(
        `${paperClass}.ft--theme--FormField--fileupload--menu::-webkit-scrollbar{width: ${_.get(styleObject, 'width', '14px')};${height}}`
      )

      classes.push(`${paperClass}.ft--theme--FormField--fileupload--menu::-webkit-scrollbar-track {
            background-color: ${scrollBarColor};
            ${scrollBarImage ? 'background-image:' + scrollBarImage : ''};
            ${Boolean(scrollBarColor) ? 'background-image: none;' : ''}
        }`)
      if (borderWidth || borderStyle || borderColor) {
        if (!borderWidth) {
          borderWidth = '1px'
        }
        if (!borderStyle) {
          borderStyle = 'solid'
        }
        if (!borderColor) {
          borderColor = 'black'
        }
      }
      classes.push(`${paperClass}.ft--theme--FormField--fileupload--menu::-webkit-scrollbar-thumb {
            background-color: ${thumbColor};
            ${thumbImage ? 'background-image:' + thumbImage : ''};
            ${Boolean(thumbColor) ? 'background-image: none;' : ''}
            ${borderRadius ? `border-radius: ${borderRadius};` : ''}
            ${borderWidth && borderColor && borderStyle ? `border: ${borderWidth} ${borderStyle} ${borderColor};` : ''}
        }`)
      extra += classes.join('\n')
    }
  }
  let borderRadius = _.get(style, 'input.borderRadius', '')
  if (borderRadius) {
    if (/^[0-9]+$/.test(borderRadius)) {
      borderRadius = `${borderRadius}px`
    }
  }

  if (_.size(style.trashIcon)) {
    let paperClass = className
    if (paperClass) {
      paperClass += '--menu'
    }
    const styles = getStyle(style.trashIcon)
    extra += `${toCSS(`${paperClass}.ft--theme--FormField--fileupload--menu .trash-icon`, getStyleObjCss(styles, '!important'), true)}`
    extra += `${toCSS(`${paperClass}.ft--theme--FormField--fileupload--menu .trash-icon path[fill="#D44646"]`, `fill:${style.trashIcon.fill} !important`, Boolean(style?.trashIcon?.fill))}`
  }
  if (_.size(style.viewIcon)) {
    let paperClass = className
    if (paperClass) {
      paperClass += '--menu'
    }
    const styles = getStyle(style.viewIcon)
    extra += `${toCSS(`${paperClass}.ft--theme--FormField--fileupload--menu .preview-icon`, getStyleObjCss(styles, '!important'), true)}`
  }

  extra += `${toCSS(
    `${className}.ft--theme--FormField--fileupload .ft--icon`,
    `color: ${_.get(style, 'iconColor', _.get(style, 'icon', className ? '' : '#000'))};
        width: ${_.get(style, 'iconWidth', className ? '' : '51')}px;
        height: ${_.get(style, 'iconHeight', className ? '' : '51')}px;
        margin-top: ${getSizeProp(_.get(style, 'iconStyle', {}), 'marginTop', 'px')};
        margin-right: ${getSizeProp(_.get(style, 'iconStyle', {}), 'marginRight', 'px')};
        margin-bottom: ${getSizeProp(_.get(style, 'iconStyle', {}), 'marginBottom', 'px')};
        margin-left: ${getSizeProp(_.get(style, 'iconStyle', {}), 'marginLeft', 'px')}`,
    _.get(style, 'icon', className ? '' : '#000'),
    _.get(style, 'iconWidth', className ? '' : '51'),
    _.get(style, 'iconHeight', className ? '' : '51'),
    _.get(style, 'iconColor', ''),
    _.get(style, ['iconStyle', 'marginTop'], className ? '' : ''),
    _.get(style, ['iconStyle', 'marginRight'], className ? '' : ''),
    _.get(style, ['iconStyle', 'marginBottom'], className ? '' : ''),
    _.get(style, ['iconStyle', 'marginLeft'], className ? '' : '')
  )}`

  if (isFieldOnly) {
    return `${renderMUITextFieldStyle(checkFields, typeStyle, defaultStyle, className)}${toCSS(
      `${checkFields.map((v) => `${className}.ft--theme--FormField--${v} .icenter-flex.ft--theme--FormField--fileupload-files`).join(',')}`,
      `color: ${_.get(style, 'labelNoFFColor', '')} !important;
    font-family: ${trimFont(_.get(style, ['labelNoFF', 'fontFamily'], className ? '' : 'cursive'))};
    font-size: ${_.get(style, ['labelNoFF', 'fontSize'], className ? '' : '12px')};
    font-weight: ${_.get(style, ['labelNoFF', 'fontWeight'], className ? '' : 'normal')};
    text-align: ${_.get(style, ['labelNoFF', 'textAlign'], className ? '' : '')};
    margin-top: ${getSizeProp(style, 'marginTop', 'px')};
            margin-right: ${getSizeProp(style, 'marginRight', 'px')};
            margin-bottom: ${getSizeProp(style, 'marginBottom', 'px')};
            margin-left: ${getSizeProp(style, 'marginLeft', 'px')};`,
      _.get(style, 'labelNoFFColor', ''),
      _.get(style, ['labelNoFF', 'fontFamily'], className ? '' : 'cursive'),
      _.get(style, ['labelNoFF', 'fontSize'], className ? '' : '12px'),
      _.get(style, ['labelNoFF', 'fontWeight'], className ? '' : 'normal'),
      _.get(style, ['labelNoFF', 'textAlign'], className ? '' : ''),
      _.get(style, ['marginTop'], className ? '' : ''),
      _.get(style, ['marginRight'], className ? '' : ''),
      _.get(style, ['marginBottom'], className ? '' : ''),
      _.get(style, ['marginLeft'], className ? '' : '')
    )}${extra}`
  } else if (fileUploadVariant === 'btn') {
    let defaultShrinkColor = _.get(style, 'primary', className ? '' : _.get(style, 'labelColor', ''))
    const styles = getStyle(_.get(style, ['inputBtn'], {}))
    const stylesHover = getStyle(_.get(style, ['inputBtnHover'], {}))
    const stylesActive = getStyle(_.get(style, ['inputBtnActive'], {}))
    return `${cssWidth}${cssHeight}${toCSS(
      `${checkFields.map((v) => `button${className}.ft--theme--FormField--${v}`).join(',')}`,
      `background-color: ${_.get(style, 'filled', className ? '' : '')};
            border-radius: ${_.get(style, 'borderRadius', className ? '' : '')}px;
            ${renderBorderStyle(style, ['input'])}
            color: ${_.get(style, 'text', '')};
            font-family: ${trimFont(_.get(style, ['input', 'fontFamily'], className ? '' : 'cursive'))};
            font-size: ${_.get(style, ['input', 'fontSize'], className ? '' : '14px')};
            font-weight: ${_.get(style, ['input', 'fontWeight'], className ? '' : 'normal')};
            ${getStyleObjCss(styles, '!important')}
            color: ${_.get(style, 'labelColorShrink', defaultShrinkColor)} !important;
            font-family: ${trimFont(_.get(style, ['labelShrink', 'fontFamily'], _.get(style, ['label', 'fontFamily'], className ? '' : '')))};
            font-size: ${_.get(style, ['labelShrink', 'fontSize'], _.get(style, ['label', 'fontSize'], className ? '' : '12px'))};
            line-height: ${_.get(style, ['labelShrink', 'lineHeight'], _.get(style, ['label', 'lineHeight'], ''))};
            font-weight: ${_.get(style, ['labelShrink', 'fontWeight'], _.get(style, ['label', 'fontWeight'], className ? '' : 'normal'))};`,
      _.get(style, 'filled', className ? '' : ''),
      _.get(style, 'borderRadius', className ? '' : ''),
      _.size(style?.input) > 0,
      _.get(style, 'text', ''),
      _.size(styles),
      _.get(style, 'labelColorShrink', defaultShrinkColor),
      _.get(style, ['labelShrink', 'fontFamily'], _.get(style, ['label', 'fontFamily'], className ? '' : '')),
      _.get(style, ['labelShrink', 'fontSize'], _.get(style, ['label', 'fontSize'], className ? '' : '12px')),
      _.get(style, ['labelShrink', 'fontWeight'], _.get(style, ['label', 'fontWeight'], className ? '' : 'normal')),
      _.get(style, ['labelShrink', 'lineHeight'], _.get(style, ['label', 'lineHeight'], ''))
    )}
        ${toCSS(
          `${checkFields.map((v) => `button${className}.ft--theme--FormField--${v} .flex`).join(',')}`,
          `justify-content: ${alignToFlex(_.get(style, ['label', 'textAlign'], ''))};flex-grow: 1;`,
          _.get(style, ['label', 'textAlign'], '')
        )}
        ${toCSS(
          `${checkFields.map((v) => `button${className}.ft--theme--FormField--${v}:hover`).join(',')}`,
          `background-color: ${_.get(style, 'inputHover.filled', className ? '' : '')};
            border-radius: ${_.get(style, 'inputHover.borderRadius', className ? '' : '')}px;
            ${renderBorderStyle(style, ['inputHover'])}
            ${getStyleObjCss(stylesHover, '!important')}`,
          _.get(style, 'inputHover.filled', className ? '' : ''),
          _.get(style, 'inputHover.borderRadius', className ? '' : ''),
          _.size(style?.inputHover) > 0,
          _.size(stylesHover)
        )}
        ${toCSS(
          `${checkFields.map((v) => `button${className}.ft--theme--FormField--${v}:active`).join(',')}`,
          `background-color: ${_.get(style, 'inputActive.filled', className ? '' : '')};
            border-radius: ${_.get(style, 'inputActive.borderRadius', className ? '' : '')}px;
            ${renderBorderStyle(style, ['inputActive'])}
            ${getStyleObjCss(stylesActive, '!important')}`,
          _.get(style, 'inputActive.filled', className ? '' : ''),
          _.get(style, 'inputActive.borderRadius', className ? '' : ''),
          _.size(style?.inputActive) > 0,
          _.size(stylesActive)
        )}
        ${toCSS(
          `${className}.ft--theme--FormField--fileupload button.ft--theme--FormField--fileupload`,
          `color: ${_.get(style, 'text', '')};
            font-family: ${trimFont(_.get(style, ['input', 'fontFamily'], className ? '' : 'cursive'))};
            font-size: ${_.get(style, ['input', 'fontSize'], className ? '' : '14px')};
            font-weight: ${_.get(style, ['input', 'fontWeight'], className ? '' : 'normal')};
            border-radius: ${_.get(style, 'input.borderRadius', '')}px;`,
          _.get(style, 'text', ''),
          _.get(style, ['input', 'fontFamily'], className ? '' : 'cursive'),
          _.get(style, ['input', 'fontSize'], className ? '' : '14px'),
          _.get(style, ['input', 'fontWeight'], className ? '' : 'normal'),
          _.get(style, 'input.borderRadius', '')
        )}${toCSS(
          `${checkFields.map((v) => `${className}.ft--theme--FormField--${v} .ft--theme--FormField--dropzoneInfo.ft--theme--FormField--fileupload-files.ft--theme--FormField--fileupload`).join(',')}`,
          `color: ${_.get(style, 'labelNoFFColor', '')} !important;
    font-family: ${trimFont(_.get(style, ['labelNoFF', 'fontFamily'], className ? '' : 'cursive'))};
    font-size: ${_.get(style, ['labelNoFF', 'fontSize'], className ? '' : '12px')};
    font-weight: ${_.get(style, ['labelNoFF', 'fontWeight'], className ? '' : 'normal')};
    text-align: ${_.get(style, ['labelNoFF', 'textAlign'], className ? '' : '')};
    margin-top: ${getSizeProp(style, 'marginTop', 'px')};
            margin-right: ${getSizeProp(style, 'marginRight', 'px')};
            margin-bottom: ${getSizeProp(style, 'marginBottom', 'px')};
            margin-left: ${getSizeProp(style, 'marginLeft', 'px')};
            padding-top: ${getSizeProp(style, 'paddingTop', 'px')};
            padding-right: ${getSizeProp(style, 'paddingRight', 'px')};
            padding-bottom: ${getSizeProp(style, 'paddingBottom', 'px')};
            padding-left: ${getSizeProp(style, 'paddingLeft', 'px')};`,
          _.get(style, 'labelNoFFColor', ''),
          _.get(style, ['labelNoFF', 'fontFamily'], className ? '' : 'cursive'),
          _.get(style, ['labelNoFF', 'fontSize'], className ? '' : '12px'),
          _.get(style, ['labelNoFF', 'fontWeight'], className ? '' : 'normal'),
          _.get(style, ['labelNoFF', 'textAlign'], className ? '' : ''),
          _.get(style, ['marginTop'], className ? '' : ''),
          _.get(style, ['marginRight'], className ? '' : ''),
          _.get(style, ['marginBottom'], className ? '' : ''),
          _.get(style, ['marginLeft'], className ? '' : ''),
          _.get(style, ['paddingTop'], className ? '' : ''),
          _.get(style, ['paddingRight'], className ? '' : ''),
          _.get(style, ['paddingBottom'], className ? '' : ''),
          _.get(style, ['paddingLeft'], className ? '' : '')
        )}${extra}`
  } else {
    return `
        ${cssWidth}
        ${toCSS(
          `${checkFields.map((v) => `${className}.ft--theme--FormField--${v} > div > div`).join(',')}`,
          `background-color: transparent;`,
          true
        )}
        ${
          iconVariant
            ? `${checkFields.map((v) => `${className}.ft--theme--FormField--${v} .ft--textContainer`).join(',')}{
            display: grid;
            width: 100%;
            height: 100%;
            grid-template-areas:${iconPosition[iconVariant]};
            grid-template-columns: 1fr 1fr 1fr;
            grid-template-columns: 1fr 1fr 1fr;
            grid-template-rows: auto auto;
            align-items: center;
            justify-content: center;
            align-content: center;
            justify-items: center;
            padding-bottom: 1.5em;
        }`
            : ''
        }
        ${labelWidth}
        ${toCSS(
          `${checkFields.map((v) => `${className}.ft--theme--FormField--${v} .ft--theme--FormField--dropzoneInfo.ft--theme--FormField--fileupload-files.ft--theme--FormField--fileupload`).join(',')}`,
          `color: ${_.get(style, 'labelNoFFColor', '')} !important;
    font-family: ${trimFont(_.get(style, ['labelNoFF', 'fontFamily'], className ? '' : 'cursive'))};
    font-size: ${_.get(style, ['labelNoFF', 'fontSize'], className ? '' : '12px')};
    font-weight: ${_.get(style, ['labelNoFF', 'fontWeight'], className ? '' : 'normal')};
    text-align: ${_.get(style, ['labelNoFF', 'textAlign'], className ? '' : '')};`,
          _.get(style, 'labelNoFFColor', ''),
          _.get(style, ['labelNoFF', 'fontFamily'], className ? '' : 'cursive'),
          _.get(style, ['labelNoFF', 'fontSize'], className ? '' : '12px'),
          _.get(style, ['labelNoFF', 'fontWeight'], className ? '' : 'normal'),
          _.get(style, ['labelNoFF', 'textAlign'], className ? '' : '')
        )}
        ${toCSS(
          `${checkFields.map((v) => `${className}.ft--theme--FormField--${v} > div`).join(',')}`,
          `background-color: ${_.get(style, 'filled', className ? '' : '')};`,
          !className && _.get(style, 'filled', className ? '' : '')
        )}
        ${toCSS(
          `${checkFields.map((v) => `${className}.ft--theme--FormField--${v} > div > div`).join(',')}`,
          `border-color: ${_.get(style, 'borderColor', className ? '' : 'rgba(0, 0, 0, 0.09)')};
            border-width: ${_.get(style, 'borderWidth', className ? '' : 'rgba(0, 0, 0, 0.09)')}px;`,
          _.get(style, 'borderColor', className ? '' : ''),
          _.get(style, 'borderWidth', className ? '' : '')
        )}
        ${toCSS(
          `${checkFields.map((v) => `${className}.ft--theme--FormField--${v} > div, ${className}.ft--theme--FormField--${v} > div > div`).join(',')}`,
          `border-radius: ${_.get(style, 'borderRadius', className ? '' : '')}px;`,
          _.get(style, 'borderRadius', className ? '' : '')
        )}
        ${toCSS(
          `${checkFields.map((v) => `${className}.ft--theme--FormField--${v} > div`).join(',')}`,
          `background-color: ${_.get(style, 'filled', className ? '' : '')};`,
          className && _.get(style, 'filled', className ? '' : '')
        )}
        ${toCSS(
          `${checkFields.map((v) => `${className}.ft--theme--FormField--${v} .ft--FormField--dropzone`).join(',')}`,
          `border-radius: ${borderRadius};${renderBorderStyle(style, ['input'])}`,
          borderRadius,
          _.size(style?.input)
        )}
        ${toCSS(
          `div.ft--FormField--dropzone-wrap${className}`,
          `border-color: ${_.get(style, 'borderColor', className ? '' : 'rgba(0, 0, 0, 0.09)')};`,
          _.get(style, 'borderColor', className ? '' : 'rgba(0, 0, 0, 0.09)')
        )}
        ${toCSS(
          `${className}.ft--theme--FormField--fileupload .ft--theme--FormField--fileupload-files, ${checkFields.map((v) => `${className}.ft--theme--FormField--${v} input, ${className}.ft--theme--FormField--${v} textarea`).join(',')}`,
          `color: ${_.get(style, 'text', '')};
            font-family: ${trimFont(_.get(style, ['input', 'fontFamily'], className ? '' : 'cursive'))};
            font-size: ${_.get(style, ['input', 'fontSize'], className ? '' : '14px')};
            font-weight: ${_.get(style, ['input', 'fontWeight'], className ? '' : 'normal')};
            margin-top: ${getSizeProp(style, 'marginTop', 'px')};
            margin-right: ${getSizeProp(style, 'marginRight', 'px')};
            margin-bottom: ${getSizeProp(style, 'marginBottom', 'px')};
            margin-left: ${getSizeProp(style, 'marginLeft', 'px')};
            `,
          _.get(style, 'text', ''),
          _.get(style, ['input', 'fontFamily'], className ? '' : 'cursive'),
          _.get(style, ['input', 'fontSize'], className ? '' : '14px'),
          _.get(style, ['input', 'fontWeight'], className ? '' : 'normal'),
          _.get(style, ['marginTop'], className ? '' : ''),
          _.get(style, ['marginRight'], className ? '' : ''),
          _.get(style, ['marginBottom'], className ? '' : ''),
          _.get(style, ['marginLeft'], className ? '' : '')
        )}
        ${cssHeight}
        ${renderMUITextFieldStyle(checkFields, typeStyle, defaultStyle, className)}${extra}`
  }
}
const renderMUIBtnGrpFieldStyle = (checkFields, typeStyle, defaultStyle, className) => {
  const style = mergeDeep({}, _.pickBy(defaultStyle, _.identity), _.pickBy(typeStyle, _.identity))
  let width = transformCSSPositionValue(
    _.get(style, 'root.width', className ? '' : ''),
    _.get(style, 'root.widthUnit', 'px'),
    true
  )
  let cssWidth = '',
    cssAlign = ''
  let cssHover = '',
    cssActive = ''
  if (width) {
    cssWidth = `${toCSS(`${checkFields.map((v) => `${className}.ft--theme--FormField--${v}.ft--theme--FormField-container`).join(',')}`, `width: ${width};max-width: 100%;`, width)}`
  }
  let wrapStyle = ''
  let wrapBorderStyle = ''
  if (style.allowWrap) {
    let col = parseInt(_.get(style, '--ff-wrap-col'), 10)
    let row = parseInt(_.get(style, '--ff-wrap-row'), 10)
    if (col || row) {
      wrapStyle = toCSS(
        `${className}.ft--theme--FormField--buttongroup .ft-FormField-btn-group-root`,
        `flex-wrap: wrap;column-gap: ${col}px;row-gap: ${row}px;`,
        row,
        col
      )
    }
    wrapBorderStyle = toCSS(
      `${className}.ft--theme--FormField--buttongroup.ft--theme--FormField--text button`,
      `border: none;`,
      true
    )
  }

  let getAlignValue = (val) => {
    if (val === 'justify') {
      return 'space-between'
    } else {
      return val
    }
  }
  if (_.get(style, ['input', 'textAlign'])) {
    cssAlign = `justify-content: ${getAlignValue(_.get(style, ['input', 'textAlign']))};`
  }

  if (_.get(style, ['inputHover', 'textAlign'])) {
    cssHover = `justify-content: ${getAlignValue(_.get(style, ['inputHover', 'textAlign']))};`
  }

  if (_.get(style, ['inputActive', 'textAlign'])) {
    cssActive = `justify-content: ${getAlignValue(_.get(style, ['inputActive', 'textAlign']))};`
  }

  let post = ''
  if (!_.isEmpty(style.icon)) {
    let styles = getStyle(style.icon)
    if (styles['--icon-pos']) {
      post += toCSS(
        `${checkFields.map((v) => `${className}.ft--theme--FormField--${v} span`).join(',')}`,
        `--icon-pos: ${styles['--icon-pos']} !important;
                --icon-pos-rtl: ${mirrorPosition(styles['--icon-pos'])} !important;`,
        true
      )
    }
    post += toCSS(
      `${checkFields.map((v) => `${className}.ft--theme--FormField--${v} .ft--theme--FormField--icon svg, ${className}.ft--theme--FormField--${v} .ft--theme--FormField--icon img`).join(',')}`,
      _.reduce(styles, (r, v, k) => [...r, `${kebabCase(k)}: ${v} !important;`], []).join(''),
      styles
    )
  }
  if (!_.isEmpty(style.iconSelected)) {
    let styles = getStyle(style.iconSelected)
    if (styles['--icon-pos']) {
      post += toCSS(
        `${checkFields.map((v) => `${className}.ft--theme--FormField--${v} button.ftselected span`).join(',')}`,
        `--icon-pos: ${styles['--icon-pos']} !important;
                --icon-pos-rtl: ${mirrorPosition(styles['--icon-pos'])} !important;`,
        true
      )
    }
    post += toCSS(
      `${checkFields.map((v) => `${className}.ft--theme--FormField--${v} button.ftselected .ft--theme--FormField--icon svg`).join(',')}`,
      _.reduce(styles, (r, v, k) => [...r, `${kebabCase(k)}: ${v} !important;`], []).join(''),
      styles
    )
    post += toCSS(
      `${checkFields.map((v) => `${className}.ft--theme--FormField--${v} button.ftselected .ft--theme--FormField--iconSelected svg`).join(',')}`,
      _.reduce(styles, (r, v, k) => [...r, `${kebabCase(k)}: ${v} !important;`], []).join(''),
      styles
    )
  }
  if (!_.isEmpty(style.iconHover)) {
    let styles = getStyle(style.iconHover)
    if (styles['--icon-pos']) {
      post += toCSS(
        `${checkFields.map((v) => `${className}.ft--theme--FormField--${v} button:hover span`).join(',')}`,
        `--icon-pos: ${styles['--icon-pos']} !important;
                --icon-pos-rtl: ${mirrorPosition(styles['--icon-pos'])} !important;`,
        true
      )
    }
    post += toCSS(
      `${checkFields.map((v) => `${className}.ft--theme--FormField--${v} button:hover .ft--theme--FormField--icon svg`).join(',')}`,
      _.reduce(styles, (r, v, k) => [...r, `${kebabCase(k)}: ${v} !important;`], []).join(''),
      styles
    )
    post += toCSS(
      `${checkFields.map((v) => `${className}.ft--theme--FormField--${v} button:hover .ft--theme--FormField--iconHover svg`).join(',')}`,
      _.reduce(styles, (r, v, k) => [...r, `${kebabCase(k)}: ${v} !important;`], []).join(''),
      styles
    )
  }
  if (!_.isEmpty(style.iconActive)) {
    let styles = getStyle(style.iconActive)
    if (styles['--icon-pos']) {
      post += toCSS(
        `${checkFields.map((v) => `${className}.ft--theme--FormField--${v} button:active span`).join(',')}`,
        `--icon-pos: ${styles['--icon-pos']} !important;
                --icon-pos-rtl: ${mirrorPosition(styles['--icon-pos'])} !important;`,
        true
      )
    }
    post += toCSS(
      `${checkFields.map((v) => `${className}.ft--theme--FormField--${v} button:active .ft--theme--FormField--icon svg`).join(',')}`,
      _.reduce(styles, (r, v, k) => [...r, `${kebabCase(k)}: ${v} !important;`], []).join(''),
      styles
    )
    post += toCSS(
      `${checkFields.map((v) => `${className}.ft--theme--FormField--${v} button:active .ft--theme--FormField--iconActive svg`).join(',')}`,
      _.reduce(styles, (r, v, k) => [...r, `${kebabCase(k)}: ${v} !important;`], []).join(''),
      styles
    )
  }
  if (!_.isEmpty(style.iconSelection)) {
    let styles = getStyle(style.iconSelection)
    post += toCSS(
      `${checkFields.map((v) => `${className}.ft--theme--FormField--${v} .ft--theme--FormField--iconSelection svg`).join(',')}`,
      _.reduce(styles, (r, v, k) => [...r, `${kebabCase(k)}: ${v};`], []).join(''),
      styles
    )
  }
  if (!_.isEmpty(style.descr)) {
    let styles = getStyle(style.descr)
    post += toCSS(
      `${checkFields.map((v) => `${className}.ft--theme--FormField--${v} button .flex-descr`).join(',')}`,
      _.reduce(styles, (r, v, k) => [...r, `${kebabCase(k)}: ${v} !important;`], []).join(''),
      styles
    )
  }

  return `${wrapStyle}
    ${cssWidth}
    ${renderMUILabelFieldStyle(checkFields, typeStyle, defaultStyle, className)}
    ${toCSS(`${className}.ft--theme--FormField--buttongroup button`, `text-transform: none;`, true)}
    ${toCSS(
      `${className}.ft--theme--FormField--buttongroup.ft--theme--FormField--outlined button`,
      `font-family: ${trimFont(_.get(style, 'input.fontFamily', className ? '' : ''))};
        font-size: ${_.get(style, 'input.fontSize', className ? '' : '')};
        font-weight: ${_.get(style, 'input.fontWeight', className ? '' : '')};
        width: ${transformCSSPositionValue(_.get(style, 'width', className ? '' : 'auto'), _.get(style, 'widthUnit', 'px'))};
        height: ${transformCSSPositionValue(_.get(style, 'height', className ? '' : ''), _.get(style, 'heightUnit', 'px'))};
        border-radius: ${transformCSSPositionValue(_.get(style, 'input.borderRadius', className ? '' : ''), 'px')};
        ${renderBorderStyle(style, ['input'])}
        margin-left: ${transformCSSPositionValue(_.get(style, 'marginLeft', className ? '' : ''), _.get(style, 'marginLeftUnit', 'px'), true)};
        margin-right: ${transformCSSPositionValue(_.get(style, 'marginRight', className ? '' : ''), _.get(style, 'marginRightUnit', 'px'), true)};
        margin-top: ${transformCSSPositionValue(_.get(style, 'marginTop', className ? '' : ''), _.get(style, 'marginTopUnit', 'px'), true)};
        margin-bottom: ${transformCSSPositionValue(_.get(style, 'marginBottom', className ? '' : ''), _.get(style, 'marginBottomUnit', 'px'), true)};
        padding-left: ${transformCSSPositionValue(_.get(style, 'paddingLeft', ''), _.get(style, 'paddingLeftUnit', 'px'), true)};
        padding-right: ${transformCSSPositionValue(_.get(style, 'paddingRight', ''), _.get(style, 'paddingRightUnit', 'px'), true)};
        padding-top: ${transformCSSPositionValue(_.get(style, 'paddingTop', ''), _.get(style, 'paddingTopUnit', 'px'), true)};
        ${cssAlign}padding-bottom: ${transformCSSPositionValue(_.get(style, 'paddingBottom', ''), _.get(style, 'paddingBottomUnit', 'px'), true)};${renderShadowCss(style?.input)}`,
      _.get(style, 'input.fontFamily', ''),
      _.get(style, 'input.textAlign', ''),
      _.get(style, 'input.fontSize', ''),
      _.get(style, 'input.fontWeight', ''),
      _.get(style, 'width', className ? '' : 'auto'),
      _.get(style, 'widthUnit', 'px'),
      _.get(style, 'height', className ? '' : ''),
      _.get(style, 'heightUnit', 'px'),
      _.get(style, 'marginLeft', className ? '' : ''),
      _.get(style, 'marginRight', className ? '' : ''),
      _.get(style, 'marginTop', className ? '' : ''),
      _.get(style, 'marginBottom', className ? '' : ''),
      _.get(style, 'paddingLeft', ''),
      _.get(style, 'paddingRight', ''),
      _.get(style, 'paddingTop', ''),
      _.get(style, 'paddingBottom', ''),
      _.get(style, 'marginLeftUnit', 'px'),
      _.get(style, 'marginRightUnit', 'px'),
      _.get(style, 'marginTopUnit', 'px'),
      _.get(style, 'marginBottomUnit', 'px'),
      _.size(style?.input)
    )}
    ${toCSS(
      `${className}.ft--theme--FormField--buttongroup.ft--theme--FormField--contained button`,
      `font-family: ${trimFont(_.get(style, 'input.fontFamily', ''))};
        font-size: ${_.get(style, 'input.fontSize', '')};
        font-weight: ${_.get(style, 'input.fontWeight', '')};
        width: ${transformCSSPositionValue(_.get(style, 'width', className ? '' : 'auto'), _.get(style, 'widthUnit', 'px'))};
        height: ${transformCSSPositionValue(_.get(style, 'height'), _.get(style, 'heightUnit', 'px'))};
        border-radius: ${transformCSSPositionValue(_.get(style, 'input.borderRadius'), 'px')};
        border-width: ${transformCSSPositionValue(_.get(style, 'input.borderWidth', ''), _.get(style, 'input.borderWidthUnit', 'px'), true)};
        border-style: ${_.get(style, 'inputHover.borderStyle')};
        border-color: ${_.get(style, 'input.primary', '')};${renderShadowCss(style?.input)};
        ${renderBorderStyle(style, ['input'])}
        margin-left: ${transformCSSPositionValue(_.get(style, 'marginLeft', ''), _.get(style, 'marginLeftUnit', 'px'), true)};
        margin-right: ${transformCSSPositionValue(_.get(style, 'marginRight', ''), _.get(style, 'marginRightUnit', 'px'), true)};
        margin-top: ${transformCSSPositionValue(_.get(style, 'marginTop', ''), _.get(style, 'marginTopUnit', 'px'), true)};
        margin-bottom: ${transformCSSPositionValue(_.get(style, 'marginBottom', ''), _.get(style, 'marginBottomUnit', 'px'), true)};
        padding-left: ${transformCSSPositionValue(_.get(style, 'paddingLeft', ''), _.get(style, 'paddingLeftUnit', 'px'), true)};
        padding-right: ${transformCSSPositionValue(_.get(style, 'paddingRight', ''), _.get(style, 'paddingRightUnit', 'px'), true)};
        padding-top: ${transformCSSPositionValue(_.get(style, 'paddingTop', ''), _.get(style, 'paddingTopUnit', 'px'), true)};
        padding-bottom: ${transformCSSPositionValue(_.get(style, 'paddingBottom', ''), _.get(style, 'paddingBottomUnit', 'px'), true)};
        ${cssAlign}`,
      _.get(style, 'input.textAlign', ''),
      _.get(style, 'input.fontFamily', ''),
      _.get(style, 'input.fontSize', ''),
      _.get(style, 'input.fontWeight', ''),
      _.get(style, 'width', className ? '' : 'auto'),
      _.get(style, 'widthUnit', 'px'),
      _.get(style, 'height', ''),
      _.get(style, 'heightUnit', 'px'),
      _.get(style, 'marginLeft', ''),
      _.get(style, 'marginRight', ''),
      _.get(style, 'marginTop', ''),
      _.get(style, 'marginBottom', ''),
      _.get(style, 'paddingLeft', ''),
      _.get(style, 'paddingRight', ''),
      _.get(style, 'paddingTop', ''),
      _.get(style, 'paddingBottom', ''),
      _.get(style, 'marginLeftUnit', 'px'),
      _.get(style, 'marginRightUnit', 'px'),
      _.get(style, 'marginTopUnit', 'px'),
      _.get(style, 'marginBottomUnit', 'px'),
      _.get(style, 'input.borderRadius', ''),
      _.get(style, 'inputHover.borderWidth', 'px'),
      _.size(style?.input)
    )}
    ${toCSS(
      `${className}.ft--theme--FormField--buttongroup.ft--theme--FormField--text button`,
      `font-family: ${trimFont(_.get(style, 'input.fontFamily', className ? '' : ''))};
        font-size: ${_.get(style, 'input.fontSize', className ? '' : '')};
        font-weight: ${_.get(style, 'input.fontWeight', className ? '' : '')};
        width: ${transformCSSPositionValue(_.get(style, 'width', className ? '' : 'auto'), _.get(style, 'widthUnit', 'px'))};
        height: ${transformCSSPositionValue(_.get(style, 'height', className ? '' : ''), _.get(style, 'heightUnit', 'px'))};
        margin-left: ${transformCSSPositionValue(_.get(style, 'marginLeft', className ? '' : ''), _.get(style, 'marginLeftUnit', 'px'), true)};
        margin-right: ${transformCSSPositionValue(_.get(style, 'marginRight', className ? '' : ''), _.get(style, 'marginRightUnit', 'px'), true)};
        margin-top: ${transformCSSPositionValue(_.get(style, 'marginTop', className ? '' : ''), _.get(style, 'marginTopUnit', 'px'), true)};
        margin-bottom: ${transformCSSPositionValue(_.get(style, 'marginBottom', className ? '' : ''), _.get(style, 'marginBottomUnit', 'px'), true)};
        padding-left: ${transformCSSPositionValue(_.get(style, 'paddingLeft', ''), _.get(style, 'paddingLeftUnit', 'px'), true)};
        padding-right: ${transformCSSPositionValue(_.get(style, 'paddingRight', ''), _.get(style, 'paddingRightUnit', 'px'), true)};
        padding-top: ${transformCSSPositionValue(_.get(style, 'paddingTop', ''), _.get(style, 'paddingTopUnit', 'px'), true)};
        ${cssAlign}padding-bottom: ${transformCSSPositionValue(_.get(style, 'paddingBottom', ''), _.get(style, 'paddingBottomUnit', 'px'), true)};${renderShadowCss(style?.input)}`,
      _.get(style, 'input.fontFamily', className ? '' : ''),
      _.get(style, 'input.textAlign', ''),
      _.get(style, 'input.fontSize', className ? '' : ''),
      _.get(style, 'input.fontWeight', className ? '' : ''),
      _.get(style, 'width', className ? '' : 'auto'),
      _.get(style, 'widthUnit', 'px'),
      _.get(style, 'height', className ? '' : ''),
      _.get(style, 'heightUnit', 'px'),
      _.get(style, 'marginLeft', className ? '' : ''),
      _.get(style, 'marginRight', className ? '' : ''),
      _.get(style, 'marginTop', className ? '' : ''),
      _.get(style, 'marginBottom', className ? '' : ''),
      _.get(style, 'paddingLeft', ''),
      _.get(style, 'paddingRight', ''),
      _.get(style, 'paddingTop', ''),
      _.get(style, 'paddingBottom', ''),
      _.get(style, 'marginLeftUnit', 'px'),
      _.get(style, 'marginRightUnit', 'px'),
      _.get(style, 'marginTopUnit', 'px'),
      _.get(style, 'marginBottomUnit', 'px')
    )}
    ${toCSS(
      `${className}.ft--theme--FormField--buttongroup.ft--theme--FormField--outlined button:hover`,
      `border-radius: ${transformCSSPositionValue(_.get(style, 'inputHover.borderRadius', className ? '' : ''), 'px')};
        ${cssHover}border-color: ${_.get(style, 'inputHover.primaryHover', className ? '' : '')};
        ${renderBorderStyle(style, ['input'])}
        ${renderBorderStyle(style, ['inputHover'])}${renderShadowCss(style?.inputHover)}`,
      _.size(style?.inputHover),
      _.get(style, 'inputHover.primaryHover', ''),
      _.get(style, 'inputHover.borderRadius', '')
    )}
    ${toCSS(
      `${className}.ft--theme--FormField--buttongroup.ft--theme--FormField--outlined button:active`,
      `border-radius: ${transformCSSPositionValue(_.get(style, 'inputActive.borderRadius', className ? '' : ''), 'px')};
        ${cssActive}border-color: ${_.get(style, 'inputActive.primaryActive', className ? '' : '')};
        ${renderBorderStyle(style, ['input'])}
        ${renderBorderStyle(style, ['inputActive'])}${renderShadowCss(style?.inputActive)}`,
      _.size(style?.inputActive),
      _.get(style, 'inputActive.primaryActive', ''),
      _.get(style, 'inputActive.borderRadius', '')
    )}
    ${toCSS(
      `${className}.ft--theme--FormField--buttongroup.ft--theme--FormField--outlined button, ${className}.ft--theme--FormField--buttongroup.ft--theme--FormField--standard button`,
      `color: ${_.get(style, 'text', className ? '' : '#000')};
         border-color: ${_.get(style, 'primary', className ? '' : '#000')};`,
      _.get(style, 'text', className ? '' : '#000'),
      _.get(style, 'primary', className ? '' : '#000')
    )}
    ${toCSS(
      `${className}.ft--theme--FormField--buttongroup.ft--theme--FormField--contained button`,
      `background-color: ${_.get(style, 'primary', className ? '' : '#000')};
        color: ${_.get(style, 'text', className ? '' : '#fff')};`,
      _.get(style, 'text', className ? '' : '#fff'),
      _.get(style, 'primary', className ? '' : '#000')
    )}
    ${toCSS(
      `${className}.ft--theme--FormField--buttongroup.ft--theme--FormField--contained button:hover`,
      `background-color: ${_.get(style, 'primaryHover', className ? '' : '')} !important;
        ${cssHover}color: ${_.get(style, 'textHover', className ? '' : '')};
        ${renderBorderStyle(style, ['input'])}
        ${renderBorderStyle(style, ['inputHover'])}${renderShadowCss(style?.inputHover)}`,
      _.get(style, 'textHover', className ? '' : ''),
      _.get(style, 'primaryHover', className ? '' : ''),
      _.size(style?.inputHover)
    )}
    ${toCSS(
      `${className}.ft--theme--FormField--buttongroup.ft--theme--FormField--contained button:active`,
      `background-color: ${_.get(style, 'primaryActive', className ? '' : '')} !important;
        ${cssActive}color: ${_.get(style, 'textActive', className ? '' : '')};
        ${renderBorderStyle(style, ['input'])}
        ${renderBorderStyle(style, ['inputActive'])}${renderShadowCss(style?.inputActive)}`,
      _.get(style, 'textActive', className ? '' : ''),
      _.get(style, 'primaryActive', className ? '' : ''),
      _.size(style?.inputActive)
    )}
    ${toCSS(
      `${className}.ft--theme--FormField--buttongroup.ft--theme--FormField--contained button.ftselected`,
      `background-color: ${_.get(style, 'primarySelected', className ? '' : '')};
        color: ${_.get(style, 'textSelected', className ? '' : '')};
        font-family: ${trimFont(_.get(style, 'input.fontFamily', className ? '' : ''))};
        font-size: ${_.get(style, 'inputSelected.fontSize', className ? '' : '')};
        font-weight: ${_.get(style, 'inputSelected.fontWeight', className ? '' : '')};
        ${renderBorderStyle(style, ['input'])}
        ${renderBorderStyle(style, ['inputSelected'])}${renderShadowCss(style?.inputSelected)}`,
      _.get(style, 'textSelected', className ? '' : ''),
      _.get(style, 'primarySelected', className ? '' : ''),
      _.size(style?.inputSelected)
    )}
    ${toCSS(
      `${className}.ft--theme--FormField--buttongroup.ft--theme--FormField--text button`,
      `color: ${_.get(style, 'primary', className ? '' : '#000')};`,
      _.get(style, 'primary', className ? '' : '#000')
    )}
    ${toCSS(
      `${className}.ft--theme--FormField--buttongroup.ft--theme--FormField--text button:hover`,
      `${cssHover}color: ${_.get(style, 'primaryHover', className ? '' : '#000')};`,
      _.get(style, 'primaryHover', className ? '' : '#000')
    )}
    ${toCSS(
      `${className}.ft--theme--FormField--buttongroup.ft--theme--FormField--text button:active`,
      `${cssActive}color: ${_.get(style, 'primaryActive', className ? '' : '#000')};`,
      _.get(style, 'primaryActive', className ? '' : '#000')
    )}
    ${toCSS(
      `${className}.ft--theme--FormField--buttongroup.ft--theme--FormField--text button.ftselected`,
      `color: ${_.get(style, 'primarySelected', className ? '' : '#000')};
        font-family: ${trimFont(_.get(style, 'input.fontFamily', className ? '' : ''))};
        font-size: ${_.get(style, 'inputSelected.fontSize', className ? '' : '')};
        font-weight: ${_.get(style, 'inputSelected.fontWeight', className ? '' : '')};${renderShadowCss(style?.inputSelected)}`,
      _.get(style, 'primarySelected', className ? '' : '#000'),
      _.size(style?.inputSelected)
    )}
    ${toCSS(
      `${className}.ft--theme--FormField--buttongroup.ft--theme--FormField--outlined button.ftselected, ${className}.ft--theme--FormField--buttongroup.ft--theme--FormField--standard button.ftselected`,
      `color: ${_.get(style, 'primarySelected', className ? '' : '#000')};
         border-color: ${_.get(style, 'primarySelected', className ? '' : '#000')};
         font-family: ${trimFont(_.get(style, 'input.fontFamily', className ? '' : ''))};
        font-size: ${_.get(style, 'inputSelected.fontSize', className ? '' : '')};
        font-weight: ${_.get(style, 'inputSelected.fontWeight', className ? '' : '')};${renderShadowCss(style?.inputSelected)}`,
      _.get(style, 'primarySelected', className ? '' : '#000'),
      _.size(style?.inputSelected)
    )}
    ${toCSS(
      `${className}.ft--theme--FormField--buttongroup.ft--theme--FormField--contained button.ftselected`,
      `background-color: ${_.get(style, 'primarySelected', className ? '' : '#000')};
        color: ${_.get(style, 'textSelected', className ? '' : '#fff')};
        font-family: ${trimFont(_.get(style, 'input.fontFamily', className ? '' : ''))};
        font-size: ${_.get(style, 'inputSelected.fontSize', className ? '' : '')};
        font-weight: ${_.get(style, 'inputSelected.fontWeight', className ? '' : '')};${renderShadowCss(style?.inputSelected)}`,
      _.get(style, 'primarySelected', className ? '' : '#000'),
      _.get(style, 'textSelected', className ? '' : '#fff'),
      _.size(style?.inputSelected)
    )}
    ${toCSS(
      `${className}.ft--theme--FormField--buttongroup button:hover`,
      `font-family: ${trimFont(_.get(style, 'inputHover.fontFamily', className ? '' : ''))};
        font-size: ${_.get(style, 'inputHover.fontSize', className ? '' : '')};
        font-weight: ${_.get(style, 'inputHover.fontWeight', className ? '' : '')};${renderShadowCss(style?.inputHover)}`,
      _.get(style, 'inputHover.fontFamily', className ? '' : ''),
      _.get(style, 'inputHover.fontSize', className ? '' : ''),
      _.get(style, 'inputHover.fontWeight', className ? '' : '')
    )}
    ${toCSS(
      `${className}.ft--theme--FormField--buttongroup button:active`,
      `font-family: ${trimFont(_.get(style, 'input.fontFamily', className ? '' : ''))};
        font-size: ${_.get(style, 'inputActive.fontSize', className ? '' : '')};
        font-weight: ${_.get(style, 'inputActive.fontWeight', className ? '' : '')};${renderShadowCss(style?.inputActive)}`,
      _.get(style, 'inputActive.fontFamily', className ? '' : ''),
      _.get(style, 'inputActive.fontSize', className ? '' : ''),
      _.get(style, 'inputActive.fontWeight', className ? '' : '')
    )}
    ${toCSS(
      `${className}.ft--theme--FormField--buttongroup.ft--theme--FormField--contained button.ftselected:hover`,
      `background-color: ${_.get(style, 'primaryHover', className ? '' : '#000')};
        color: ${_.get(style, 'textHover', className ? '' : '')};`,
      _.get(style, 'primaryHover', className ? '' : ''),
      _.get(style, 'textHover', className ? '' : '')
    )}
    ${toCSS(
      `${className}.ft--theme--FormField--buttongroup.ft--theme--FormField--contained button.ftselected:active`,
      `background-color: ${_.get(style, 'primaryActive', className ? '' : '')};
        color: ${_.get(style, 'textActive', className ? '' : '')};`,
      _.get(style, 'primaryActive', className ? '' : ''),
      _.get(style, 'textActive', className ? '' : '')
    )}${wrapBorderStyle}${post}`
}
const renderMUISliderFieldStyle = (checkFields, typeStyle, defaultStyle, className) => {
  const style = mergeDeep({}, _.pickBy(defaultStyle, _.identity), _.pickBy(typeStyle, _.identity))

  const shadow = isHex(_.get(style, 'primary', className ? '' : ''))
    ? String(_.get(style, 'primary', className ? '' : '')).substr(0, 7) + '26'
    : _.get(style, 'primary', className ? '' : '')

  let extra = ''
  if (checkFields.indexOf('slider') !== -1) {
    if (style.sliderLineColor) {
      extra += toCSS(
        `${className}.ft--theme--FormField--slider .ftrail, ${className}.ft--theme--FormField--slider .fttrack`,
        `background-color: ${_.get(style, 'sliderLineColor', className ? '' : '')} !important;`,
        _.get(style, 'sliderLineColor', className ? '' : '')
      )
    }

    if (style.sliderHandleColor) {
      extra += toCSS(
        `${className}.ft--theme--FormField--slider .ftthumb`,
        `background-color: ${_.get(style, 'sliderHandleColor', className ? '' : '')} !important;`,
        _.get(style, 'sliderHandleColor', className ? '' : '')
      )
    }
    if (style.HandleStrokeColor) {
      extra += toCSS(
        `${className}.ft--theme--FormField--slider .ftthumb`,
        `border: 1px solid ${_.get(style, 'HandleStrokeColor', '')} !important;`,
        _.get(style, 'HandleStrokeColor', '')
      )
    }
    if (style.bubbleColor) {
      extra += toCSS(
        `${className}.ft--theme--FormField--slider .ftvalueLabel`,
        `color: ${_.get(style, 'bubbleColor', className ? '' : '')} !important;`,
        _.get(style, 'bubbleColor', className ? '' : '')
      )
    }
  }
  let width = transformCSSPositionValue(
    _.get(style, 'root.width', className ? '' : ''),
    _.get(style, 'root.widthUnit', 'px'),
    true
  )
  let cssWidth = ''
  if (width) {
    cssWidth = `${toCSS(`${checkFields.map((v) => `${className}.ft--theme--FormField--${v}.ft--theme--FormField-container`).join(',')}`, `width: ${width};`, width)}`
  }
  return `
        ${cssWidth}
        ${renderMUILabelFieldStyle(checkFields, typeStyle, defaultStyle, className, true)}${toCSS(
          `${className}.ft--theme--FormField--slider .fttrack, ${className}.ft--theme--FormField--slider .ftthumb, ${className}.ft--theme--FormField--slider .ftmark, ${className}.ft--theme--FormField--slider .ftrail`,
          `background-color: ${_.get(style, 'primary', className ? '' : '')}!important;`,
          _.get(style, 'primary', className ? '' : '')
        )}
    ${toCSS(
      `${className}.ft--theme--FormField--slider .ftvalueLabel`,
      `color: ${_.get(style, 'primary', className ? '' : '')}!important;`,
      _.get(style, 'primary', className ? '' : '')
    )}
    ${toCSS(
      `${className}.ft--theme--FormField--slider .ftthumb:hover, ${className}.ft--theme--FormField--slider .ftthumb, ${className}.ft--theme--FormField--slider .ftactive`,
      `box-shadow: none !important;`,
      _.get(style, 'primary', className ? '' : '')
    )}
    ${extra}`
}

const renderMUICheckFieldStyle = (checkFields, typeStyle, defaultStyle, className) => {
  const style = mergeDeep({}, deepClean(defaultStyle), deepClean(typeStyle))
  let extra = ''
  if (checkFields.indexOf('radio') !== -1) {
    if (style.radiotickMarkColor) {
      extra += toCSS(
        `${className}.ft--theme--FormField--radio svg, ${className}.ft--theme--FormField--radio svg,
            ${className}.ft--theme--FormField--radio .ftchecked svg, ${className}.ft--theme--FormField--radio .ftchecked svg`,
        `color: ${_.get(style, 'radiotickMarkColor', className ? '' : '')} !important;`,
        _.get(style, 'radiotickMarkColor', className ? '' : '')
      )
    }
  }
  if (checkFields.indexOf('checkbox') !== -1) {
    if (style.checkboxtickMarkColor) {
      extra += toCSS(
        `${className}.ft--theme--FormField--checkbox svg, ${className}.ft--theme--FormField--checkbox svg,
                ${className}.ft--theme--FormField--checkbox .ftchecked svg, ${className}.ft--theme--FormField--checkbox .ftchecked svg`,
        `color: ${_.get(style, 'checkboxtickMarkColor', className ? '' : '')} !important;`,
        _.get(style, 'checkboxtickMarkColor', className ? '' : '')
      )
    }
  }
  if (checkFields.indexOf('switcher') !== -1 || checkFields.indexOf('switch') !== -1) {
    if (style.switchLineColorOff) {
      extra += toCSS(
        `${className}.ft--theme--FormField--switch .fttrack`,
        `background-color: ${_.get(style, 'switchLineColorOff', className ? '' : '')} !important;opacity: 1;`,
        _.get(style, 'switchLineColorOff', className ? '' : '')
      )
    }
    if (style.switchLineColor) {
      extra += toCSS(
        `${className}.ft--theme--FormField--switch.ft-checked .fttrack`,
        `background-color: ${_.get(style, 'switchLineColor', className ? '' : '')} !important;opacity: 1;`,
        _.get(style, 'switchLineColor', className ? '' : '')
      )
    }
    if (style.switchHandleColorOff) {
      extra += toCSS(
        `${className}.ft--theme--FormField--switch .ftthumb`,
        `background-color: ${_.get(style, 'switchHandleColorOff', className ? '' : '')} !important;`,
        _.get(style, 'switchHandleColorOff', className ? '' : '')
      )
    } else if (style.switcherHandleColorOff) {
      extra += toCSS(
        `${className}.ft--theme--FormField--switch .ftthumb`,
        `background-color: ${_.get(style, 'switcherHandleColorOff', className ? '' : '')} !important;`,
        _.get(style, 'switcherHandleColorOff', className ? '' : '')
      )
    }
    if (style.switchHandleColor) {
      extra += toCSS(
        `${className}.ft--theme--FormField--switch.ft-checked .ftthumb`,
        `background-color: ${_.get(style, 'switchHandleColor', className ? '' : '')} !important;`,
        _.get(style, 'switchHandleColor', className ? '' : '')
      )
    } else if (style.switcherHandleColor) {
      extra += toCSS(
        `${className}.ft--theme--FormField--switch.ft-checked .ftthumb`,
        `background-color: ${_.get(style, 'switcherHandleColor', className ? '' : '')} !important;`,
        _.get(style, 'switcherHandleColor', className ? '' : '')
      )
    }
  }
  let width = transformCSSPositionValue(
    _.get(style, 'root.width', className ? '' : ''),
    _.get(style, 'root.widthUnit', 'px'),
    true
  )
  let cssWidth = ''
  if (width) {
    cssWidth = `${toCSS(`${checkFields.map((v) => `${className}.ft--theme--FormField--${v}.ft--theme--FormField-container`).join(',')}`, `width: ${width};`, width)}`
  }
  let icoStyles = ''
  if (checkFields.includes('radio') || checkFields.includes('checkbox')) {
    let w = parseInt(_.get(style, '--ff-icon-width'), 10)
    let h = parseInt(_.get(style, '--ff-icon-height'), 10)
    let text = ''
    if (w) {
      text += `--ff-icon-width: ${w}px;`
    }
    if (h) {
      text += `--ff-icon-height: ${h}px;`
    }
    icoStyles = toCSS(
      `${checkFields.map((v) => `${className}.ft--theme--FormField--${v} .ft--ff-ci-wrap`).join(',')}`,
      text,
      w,
      h
    )
  }

  try {
    if (className && (checkFields.includes('radio') || checkFields.includes('checkbox'))) {
      if (!style.captionColor) {
        let defaultShrinkColor = _.get(style, 'primary', className ? '' : _.get(style, 'labelColor', ''))
        style.captionColor = _.get(style, 'labelColorShrink', defaultShrinkColor)
      }
      if (!_.get(style, 'caption.fontSize', '')) {
        _.set(
          style,
          'caption.fontSize',
          _.get(style, ['labelShrink', 'fontSize'], _.get(style, ['label', 'fontSize'], ''))
        )
      }
      if (!_.get(style, 'caption.fontFamily', '')) {
        _.set(
          style,
          'caption.fontFamily',
          _.get(style, ['labelShrink', 'fontFamily'], _.get(style, ['label', 'fontFamily'], ''))
        )
      }
      if (!_.get(style, 'caption.fontWeight', '')) {
        _.set(
          style,
          'caption.fontWeight',
          _.get(style, ['labelShrink', 'fontWeight'], _.get(style, ['label', 'fontWeight'], ''))
        )
      }
      if (!_.get(style, 'caption.lineHeight', '')) {
        _.set(
          style,
          'caption.lineHeight',
          _.get(style, ['labelShrink', 'lineHeight'], _.get(style, ['label', 'lineHeight'], ''))
        )
      }
      if (!_.get(style, 'caption.textAlign', '')) {
        _.set(
          style,
          'caption.textAlign',
          _.get(style, ['labelShrink', 'textAlign'], _.get(style, ['label', 'textAlign'], ''))
        )
      }
    }
  } catch (error) {
    console.error(error.stack)
  }
  if (_.size(style.caption) || style.captionColor || (className ? '' : '#000')) {
    if (style.caption && style.caption.alignItems) {
      extra += toCSS(
        checkFields.map((v) => `${className}.ft--theme--FormField--${v} .ft-checkbox-control-wrap`).join(','),
        `align-items: ${style.caption.alignItems};`,
        _.get(style, 'caption.alignItems', '')
      )
      extra += toCSS(
        checkFields
          .map((v) => `${className}.ft--theme--FormField--${v} .ft-checkbox-control-wrap span:first-child`)
          .join(','),
        `padding-top:0;padding-bottom:0;`,
        _.get(style, 'caption.alignItems', '')
      )
    }
    extra += toCSS(
      checkFields.map((v) => `${className}.ft--theme--FormField--${v} .ft-caption`).join(','),
      `color: ${_.get(style, 'captionColor', className ? '' : '#000')} !important;
            font-size: ${_.get(style, 'caption.fontSize', '')} !important;
            font-family: ${_.get(style, 'caption.fontFamily', '')} !important;
            font-weight: ${_.get(style, 'caption.fontWeight', '')} !important;
            padding-left: ${transformCSSPositionValue(_.get(style, 'caption.paddingLeft', ''), _.get(style, 'caption.paddingLeftUnit', 'px'), true)} !important;
            padding-right: ${transformCSSPositionValue(_.get(style, 'caption.paddingRight', ''), _.get(style, 'caption.paddingRightUnit', 'px'), true)} !important;
            padding-top: ${transformCSSPositionValue(_.get(style, 'caption.paddingTop', ''), _.get(style, 'caption.paddingTopUnit', 'px'), true)} !important;
            padding-bottom: ${transformCSSPositionValue(_.get(style, 'caption.paddingBottom', ''), _.get(style, 'caption.paddingBottomUnit', 'px'), true)} !important;
            margin-left: ${transformCSSPositionValue(_.get(style, 'caption.marginLeft', ''), _.get(style, 'caption.marginLeftUnit', 'px'), true)} !important;
            margin-right: ${transformCSSPositionValue(_.get(style, 'caption.marginRight', ''), _.get(style, 'caption.marginRightUnit', 'px'), true)} !important;
            margin-top: ${transformCSSPositionValue(_.get(style, 'caption.marginTop', ''), _.get(style, 'caption.marginTopUnit', 'px'), true)} !important;
            margin-bottom: ${transformCSSPositionValue(_.get(style, 'caption.marginBottom', ''), _.get(style, 'caption.marginBottomUnit', 'px'), true)} !important;`,
      _.get(style, 'captionColor', className ? '' : '#000'),
      _.get(style, 'caption.paddingLeft', ''),
      _.get(style, 'caption.paddingLeftUnit', 'px'),
      _.get(style, 'caption.paddingRight', ''),
      _.get(style, 'caption.paddingRightUnit', 'px'),
      _.get(style, 'caption.paddingTop', ''),
      _.get(style, 'caption.paddingTopUnit', 'px'),
      _.get(style, 'caption.paddingBottom', ''),
      _.get(style, 'caption.paddingBottomUnit', 'px'),
      _.get(style, 'caption.marginLeft', ''),
      _.get(style, 'caption.marginLeftUnit', 'px'),
      _.get(style, 'caption.marginRight', ''),
      _.get(style, 'caption.marginRightUnit', 'px'),
      _.get(style, 'caption.marginTop', ''),
      _.get(style, 'caption.marginTopUnit', 'px'),
      _.get(style, 'caption.marginBottom', ''),
      _.get(style, 'caption.marginBottomUnit', 'px'),
      _.get(style, 'caption.fontFamily', ''),
      _.get(style, 'caption.fontSize', ''),
      _.get(style, 'caption.fontWeight', '')
    )
  }
  if (_.size(style.captionSelected) || style.captionColorSelected) {
    if (style.captionSelected && style.captionSelected.alignItems) {
      extra += toCSS(
        checkFields.map((v) => `${className}.ft--theme--FormField--${v} .ft-checkbox-control-wrap`).join(','),
        `align-items: ${style.captionSelected.alignItems};`,
        _.get(style, 'captionSelected.alignItems', '')
      )
      extra += toCSS(
        checkFields
          .map((v) => `${className}.ft--theme--FormField--${v} .ft-checkbox-control-wrap span:first-child`)
          .join(','),
        `padding-top:0;padding-bottom:0;`,
        _.get(style, 'captionSelected.alignItems', '')
      )
    }
    extra += toCSS(
      checkFields.map((v) => `${className}.ft--theme--FormField--${v} .ft-caption.captionSelected`).join(','),
      `color: ${_.get(style, 'captionColorSelected', '')} !important;
            font-size: ${_.get(style, 'captionSelected.fontSize', '')} !important;
            font-family: ${_.get(style, 'captionSelected.fontFamily', '')} !important;
            font-weight: ${_.get(style, 'captionSelected.fontWeight', '')} !important;
            padding-left: ${transformCSSPositionValue(_.get(style, 'captionSelected.paddingLeft', ''), _.get(style, 'captionSelected.paddingLeftUnit', 'px'), true)} !important;
            padding-right: ${transformCSSPositionValue(_.get(style, 'captionSelected.paddingRight', ''), _.get(style, 'captionSelected.paddingRightUnit', 'px'), true)} !important;
            padding-top: ${transformCSSPositionValue(_.get(style, 'captionSelected.paddingTop', ''), _.get(style, 'captionSelected.paddingTopUnit', 'px'), true)} !important;
            padding-bottom: ${transformCSSPositionValue(_.get(style, 'captionSelected.paddingBottom', ''), _.get(style, 'captionSelected.paddingBottomUnit', 'px'), true)} !important;
            margin-left: ${transformCSSPositionValue(_.get(style, 'captionSelected.marginLeft', ''), _.get(style, 'captionSelected.marginLeftUnit', 'px'), true)} !important;
            margin-right: ${transformCSSPositionValue(_.get(style, 'captionSelected.marginRight', ''), _.get(style, 'captionSelected.marginRightUnit', 'px'), true)} !important;
            margin-top: ${transformCSSPositionValue(_.get(style, 'captionSelected.marginTop', ''), _.get(style, 'captionSelected.marginTopUnit', 'px'), true)} !important;
            margin-bottom: ${transformCSSPositionValue(_.get(style, 'captionSelected.marginBottom', ''), _.get(style, 'captionSelected.marginBottomUnit', 'px'), true)} !important;`,
      _.get(style, 'captionColorSelected', ''),
      _.get(style, 'captionSelected.paddingLeft', ''),
      _.get(style, 'captionSelected.paddingLeftUnit', 'px'),
      _.get(style, 'captionSelected.paddingRight', ''),
      _.get(style, 'captionSelected.paddingRightUnit', 'px'),
      _.get(style, 'captionSelected.paddingTop', ''),
      _.get(style, 'captionSelected.paddingTopUnit', 'px'),
      _.get(style, 'captionSelected.paddingBottom', ''),
      _.get(style, 'captionSelected.paddingBottomUnit', 'px'),
      _.get(style, 'captionSelected.marginLeft', ''),
      _.get(style, 'captionSelected.marginLeftUnit', 'px'),
      _.get(style, 'captionSelected.marginRight', ''),
      _.get(style, 'captionSelected.marginRightUnit', 'px'),
      _.get(style, 'captionSelected.marginTop', ''),
      _.get(style, 'captionSelected.marginTopUnit', 'px'),
      _.get(style, 'captionSelected.marginBottom', ''),
      _.get(style, 'captionSelected.marginBottomUnit', 'px'),
      _.get(style, 'captionSelected.fontFamily', ''),
      _.get(style, 'captionSelected.fontSize', ''),
      _.get(style, 'captionSelected.fontWeight', '')
    )
  }
  let colWrap = ''
  let rowWrap = ''
  if (style['--ff-wrap-col']) {
    colWrap = transformCSSPositionValue(style['--ff-wrap-col'], 'px', true)
  }
  if (style['--ff-wrap-row']) {
    rowWrap = transformCSSPositionValue(style['--ff-wrap-row'], 'px', true)
  }
  return `${icoStyles}
    ${cssWidth}
    ${renderMUILabelFieldStyle(checkFields, typeStyle, defaultStyle, className)}${toCSS(
      checkFields.map((v) => `${className}.ft--theme--FormField--${v} svg`).join(','),
      `color: ${_.get(style, 'text', className ? '' : '')};`,
      _.get(style, 'text', className ? '' : '')
    )}
    ${toCSS(
      checkFields.map((v) => `${className}.ft--theme--FormField--${v} .ftchecked svg`).join(','),
      `color: ${_.get(style, 'primary', className ? '' : '')};`,
      _.get(style, 'primary', className ? '' : '')
    )}
    ${toCSS(
      checkFields
        .map(
          (v) =>
            `${className}.ft--theme--FormField--${v}.ft-checked .fttrack, ${className}.ft--theme--FormField--${v}.ft-checked .ftthumb`
        )
        .join(','),
      `background-color: ${_.get(style, 'primary', className ? '' : '')}!important;`,
      _.get(style, 'primary', className ? '' : '')
    )}
    ${toCSS(
      checkFields.map((v) => `${className}.ft--theme--FormField--${v} [role="radiogroup"]`).join(','),
      `--ff-wrap-col: ${colWrap};
        --ff-wrap-row: ${rowWrap};`,
      colWrap,
      rowWrap
    )}
    ${extra}`
}

const renderMUIDropDownFieldStyle = (textFields, typeStyle, defaultStyle, className) => {
  let style = mergeDeep({}, _.pickBy(defaultStyle, _.identity), _.pickBy(typeStyle, _.identity))
  let extra = ''
  if (style.iconClosed) {
    let styles = getStyle(style.iconClosed)
    if (!_.isEmpty(styles)) {
      extra += toCSS(
        `${className} .ft--theme--FormField--cicon-close svg`,
        _.reduce(styles, (r, v, k) => [...r, `${kebabCase(k)}: ${v};`], []).join(''),
        true
      )
    }
  }
  if (style.icon) {
    let styles = getStyle(style.icon)
    if (!_.isEmpty(styles)) {
      let cssVar = ''
      if (styles.fill) {
        cssVar = `--ft-icon-fill:${styles.fill};`
      }
      extra += toCSS(
        `${className} .ft--theme--FormField--cicon svg`,
        _.reduce(styles, (r, v, k) => [...r, `${kebabCase(k)}: ${v};`], []).join('') + cssVar,
        true
      )
    }
  }
  if (style.scroll) {
    let scrollStyles = getScrollBarStyles(style.scroll)
    extra += toCSS(
      `${className} .ft--theme--FormField--scroll`,
      _.map(scrollStyles, (val, key) => `${kebabCase(key)}:${_.trim(val, ';')}`).join(';\n'),
      true
    )
  }
  style = mergeDeep({}, _.pickBy(defaultStyle, _.identity), _.pickBy(typeStyle, _.identity))

  if (
    style.dropdownItemFill ||
    _.size(_.get(style, 'item', {})) > 0 ||
    _.size(_.get(style, 'inputItem', {})) > 0 ||
    _.get(style, 'textItem', className ? '' : '#000')
  ) {
    let paperClass = className
    if (paperClass) {
      paperClass += '--option'
    }
    let color = _.get(style, 'textItem', className ? '' : '#000')
    if (
      (_.get(style, 'dropdownListFill', color) === color || style.dropdownItemFill === color) &&
      _.startsWith(_.toLower(color), '#ffffff')
    ) {
      color = '#000000'
    }
    extra += toCSS(
      `${paperClass}.ft--theme--FormField--picklist--option${textFields.map((v) => `${paperClass}.ft--theme--FormField--picklist--option${v}`).join(',')}`,
      `background-color: ${_.get(style, 'dropdownItemFill', className ? '' : '')};
            color: ${color};
            font-size: ${_.get(style, 'inputItem.fontSize', '')};
            font-family: ${_.get(style, 'inputItem.fontFamily', '')};
            font-weight: ${_.get(style, 'inputItem.fontWeight', '')};
            border-width: ${transformCSSPositionValue(_.get(style, 'item.borderWidth', className ? '' : ''), _.get(style, 'item.borderWidthUnit', 'px'))};
            border-style: ${_.get(style, 'item.borderStyle', className ? '' : '')};
            border-color: ${_.get(style, 'item.borderColor', className ? '' : '')};
            padding-left: ${transformCSSPositionValue(_.get(style, 'item.paddingLeft', ''), _.get(style, 'item.paddingLeftUnit', 'px'), true)};
        padding-right: ${transformCSSPositionValue(_.get(style, 'item.paddingRight', ''), _.get(style, 'item.paddingRightUnit', 'px'), true)};
        padding-top: ${transformCSSPositionValue(_.get(style, 'item.paddingTop', ''), _.get(style, 'item.paddingTopUnit', 'px'), true)};
        padding-bottom: ${transformCSSPositionValue(_.get(style, 'item.paddingBottom', ''), _.get(style, 'item.paddingBottomUnit', 'px'), true)};max-width: 100%;`,
      _.get(style, 'dropdownItemFill', className ? '' : ''),
      color,
      _.get(style, 'inputItem.fontFamily', ''),
      _.get(style, 'inputItem.fontSize', ''),
      _.get(style, 'inputItem.fontWeight', ''),
      _.get(style, 'item.borderWidth', ''),
      _.get(style, 'item.borderStyle', ''),
      _.get(style, 'item.borderColor', ''),
      _.get(style, 'item.paddingLeft', ''),
      _.get(style, 'item.paddingLeftUnit', 'px'),
      _.get(style, 'item.paddingRight', ''),
      _.get(style, 'item.paddingRightUnit', 'px'),
      _.get(style, 'item.paddingTop', ''),
      _.get(style, 'item.paddingTopUnit', 'px'),
      _.get(style, 'item.paddingBottom', ''),
      _.get(style, 'item.paddingBottomUnit', 'px')
    )
  }

  if (style.dropdownListFill || _.size(style.menu) > 0 || _.get(style, 'dropdownListFill', '')) {
    let paperClass = className
    if (paperClass) {
      paperClass += '--menu'
    }
    let borderRadius = _.get(style, 'menu.borderRadius', '')
    if (/^[0-9]+$/.test(borderRadius)) {
      borderRadius = `${borderRadius}px`
    }
    const extraStyle = getStyleObjCss(
      getStyle(
        _.pick(style?.menu || {}, [
          'width',
          'widthUnit',
          'minWidth',
          'minWidthUnit',
          'maxWidth',
          'maxWidthUnit',
          'height',
          'heightUnit',
          'minHeight',
          'minHeightUnit',
          'maxHeight',
          'maxHeightUnit',
          'marginLeft',
          'marginLeftUnit',
          'marginRight',
          'marginRightUnit',
          'marginTop',
          'marginTopUnit',
          'marginBottom',
          'marginBottomUnit',
        ])
      )
    )
    extra += toCSS(
      `${paperClass}.ft--theme--FormField--picklist--menu`,
      `background-color: ${_.get(style, 'dropdownListFill', '')} !important;
            border-width: ${transformCSSPositionValue(_.get(style, 'menu.borderWidth', ''), _.get(style, 'menu.borderWidthUnit', 'px'))};
            border-style: ${_.get(style, 'menu.borderStyle', '')};
            border-color: ${_.get(style, 'menu.borderColor', '')};
            padding-left: ${transformCSSPositionValue(_.get(style, 'menu.paddingLeft', ''), _.get(style, 'menu.paddingLeftUnit', 'px'), true)};
            padding-right: ${transformCSSPositionValue(_.get(style, 'menu.paddingRight', ''), _.get(style, 'menu.paddingRightUnit', 'px'), true)};
            padding-top: ${transformCSSPositionValue(_.get(style, 'menu.paddingTop', ''), _.get(style, 'menu.paddingTopUnit', 'px'), true)};
            padding-bottom: ${transformCSSPositionValue(_.get(style, 'menu.paddingBottom', ''), _.get(style, 'menu.paddingBottomUnit', 'px'), true)};
            border-radius: ${borderRadius};${extraStyle};`,
      _.get(style, 'dropdownListFill', ''),
      _.get(style, 'menu.borderWidth', ''),
      _.get(style, 'menu.borderStyle', ''),
      _.get(style, 'menu.borderColor', ''),
      _.get(style, 'menu.paddingLeft', ''),
      _.get(style, 'menu.paddingLeftUnit', 'px'),
      _.get(style, 'menu.paddingRight', ''),
      _.get(style, 'menu.paddingRightUnit', 'px'),
      _.get(style, 'menu.paddingTop', ''),
      _.get(style, 'menu.paddingTopUnit', 'px'),
      _.get(style, 'menu.paddingBottom', ''),
      _.get(style, 'menu.paddingBottomUnit', 'px'),
      borderRadius,
      !!extraStyle
    )
  }

  if (
    style.dropdownItemFillSelected ||
    _.get(style, 'textItemSelected', '') ||
    _.size(_.get(style, 'itemSelected', {}))
  ) {
    let paperClass = className
    if (paperClass) {
      paperClass += '--option'
    }
    extra += toCSS(
      `${paperClass}.ft--theme--FormField--picklist--option${textFields.map((v) => `${paperClass}.ft--theme--FormField--picklist--option${v}`).join(',')}[aria-selected="true"], ${paperClass}.ft--theme--FormField--picklist--option${textFields.map((v) => `${paperClass}.ft--theme--FormField--picklist--option${v}`).join(',')}[aria-selected="true"]`,
      `color: ${_.get(style, 'textItemSelected', '')};
            font-size: ${_.get(style, 'inputItemSelected.fontSize', '')};
            font-family: ${_.get(style, 'inputItemSelected.fontFamily', '')};
            font-weight: ${_.get(style, 'inputItemSelected.fontWeight', '')};
            background-color: ${_.get(style, 'dropdownItemFillSelected', '')};
            border-width: ${transformCSSPositionValue(_.get(style, 'itemSelected.borderWidth', ''), _.get(style, 'itemSelected.borderWidthUnit', 'px'))};
            border-style: ${_.get(style, 'itemSelected.borderStyle', '')};
            border-color: ${_.get(style, 'itemSelected.borderColor', '')};
            padding-left: ${transformCSSPositionValue(_.get(style, 'itemSelected.paddingLeft', ''), _.get(style, 'itemSelected.paddingLeftUnit', 'px'), true)};
        padding-right: ${transformCSSPositionValue(_.get(style, 'itemSelected.paddingRight', ''), _.get(style, 'itemSelected.paddingRightUnit', 'px'), true)};
        padding-top: ${transformCSSPositionValue(_.get(style, 'itemSelected.paddingTop', ''), _.get(style, 'itemSelected.paddingTopUnit', 'px'), true)};
        padding-bottom: ${transformCSSPositionValue(_.get(style, 'itemSelected.paddingBottom', ''), _.get(style, 'itemSelected.paddingBottomUnit', 'px'), true)};`,
      _.get(style, 'dropdownItemFillSelected', ''),
      !_.isEmpty(_.get(style, 'itemSelected', {})),
      !_.isEmpty(_.get(style, 'inputItemSelected', {})),
      _.get(style, 'textItemSelected', '')
    )
  }

  if (
    style.dropdownItemFillHover ||
    _.size(_.get(style, 'textItemHover', '')) ||
    _.size(_.get(style, 'itemHover', {}))
  ) {
    let paperClass = className
    if (paperClass) {
      paperClass += '--option'
    }
    extra += toCSS(
      `${paperClass}.ft--theme--FormField--picklist--option${textFields.map((v) => `${paperClass}.ft--theme--FormField--picklist--option${v}`).join(',')}:hover, ${paperClass}.ft--theme--FormField--picklist--option${textFields.map((v) => `${paperClass}.ft--theme--FormField--picklist--option${v}`).join(',')}.active, ${paperClass}.ft--theme--FormField--picklist--option${textFields.map((v) => `${paperClass}.ft--theme--FormField--picklist--option${v}`).join(',')}:focus, ${paperClass}.ft--theme--FormField--picklist--option.Mui-selected, ${paperClass}.ft--theme--FormField--picklist--option:focus-visible`,
      `color: ${_.get(style, 'textItemHover', '')} !important;
            font-size: ${_.get(style, 'inputItemHover.fontSize', '')};
            font-family: ${_.get(style, 'inputItemHover.fontFamily', '')};
            font-weight: ${_.get(style, 'inputItemHover.fontWeight', '')};
            background-color: ${_.get(style, 'dropdownItemFillHover', '')} !important;
            border-width: ${transformCSSPositionValue(_.get(style, 'itemHover.borderWidth', ''), _.get(style, 'itemHover.borderWidthUnit', 'px'))};
            border-style: ${_.get(style, 'itemHover.borderStyle', '')};
            border-color: ${_.get(style, 'itemHover.borderColor', '')};
            padding-left: ${transformCSSPositionValue(_.get(style, 'itemHover.paddingLeft', ''), _.get(style, 'itemHover.paddingLeftUnit', 'px'), true)};
        padding-right: ${transformCSSPositionValue(_.get(style, 'itemHover.paddingRight', ''), _.get(style, 'itemHover.paddingRightUnit', 'px'), true)};
        padding-top: ${transformCSSPositionValue(_.get(style, 'itemHover.paddingTop', ''), _.get(style, 'itemHover.paddingTopUnit', 'px'), true)};
        padding-bottom: ${transformCSSPositionValue(_.get(style, 'itemHover.paddingBottom', ''), _.get(style, 'itemHover.paddingBottomUnit', 'px'), true)};`,
      _.get(style, 'dropdownItemFillHover', ''),
      _.get(style, 'itemHover.borderWidth', ''),
      !_.isEmpty(_.get(style, 'itemHover', {})),
      !_.isEmpty(_.get(style, 'inputItemHover', {})),
      _.get(style, 'textItemHover', '')
    )
  }

  if (style.dropdownItemFillActive || _.get(style, 'textItemActive', '') || _.size(_.get(style, 'itemActive', {}))) {
    let paperClass = className
    if (paperClass) {
      paperClass += '--option'
    }
    extra += toCSS(
      `${paperClass}.ft--theme--FormField--picklist--option${textFields.map((v) => `${paperClass}.ft--theme--FormField--picklist--option${v}`).join(',')}:active`,
      `color: ${_.get(style, 'textItemActive', '')} !important;
            font-size: ${_.get(style, 'inputItemActive.fontSize', '')};
            font-family: ${_.get(style, 'inputItemActive.fontFamily', '')};
            font-weight: ${_.get(style, 'inputItemActive.fontWeight', '')};
            background-color: ${_.get(style, 'dropdownItemFillActive', '')} !important;
            border-width: ${transformCSSPositionValue(_.get(style, 'itemActive.borderWidth', ''), _.get(style, 'itemActive.borderWidthUnit', 'px'))};
            border-style: ${_.get(style, 'itemActive.borderStyle', '')};
            border-color: ${_.get(style, 'itemActive.borderColor', '')};
            padding-left: ${transformCSSPositionValue(_.get(style, 'itemActive.paddingLeft', ''), _.get(style, 'itemActive.paddingLeftUnit', 'px'), true)};
        padding-right: ${transformCSSPositionValue(_.get(style, 'itemActive.paddingRight', ''), _.get(style, 'itemActive.paddingRightUnit', 'px'), true)};
        padding-top: ${transformCSSPositionValue(_.get(style, 'itemActive.paddingTop', ''), _.get(style, 'itemActive.paddingTopUnit', 'px'), true)};
        padding-bottom: ${transformCSSPositionValue(_.get(style, 'itemActive.paddingBottom', ''), _.get(style, 'itemActive.paddingBottomUnit', 'px'), true)};`,
      _.get(style, 'dropdownItemFillActive', ''),
      !_.isEmpty(_.get(style, 'itemActive', {})),
      !_.isEmpty(_.get(style, 'inputItemActive', {})),
      _.get(style, 'textItemActive', '')
    )
  }

  _.each(['validIcon', 'invalidIcon'], (key) => {
    if (!_.isEmpty(style[key])) {
      let styles = getStyle(style[key])
      extra += toCSS(
        `${textFields.map((v) => `${className}.ft--theme--FormField--${v} .ft--theme--FormField--${key} svg`).join(',')}`,
        _.reduce(styles, (r, v, k) => [...r, `${kebabCase(k)}: ${v};`], []).join(''),
        styles
      )
      let ioniconStyles = getStyle(style[key])
      if (ioniconStyles.fill) {
        ioniconStyles.color = ioniconStyles.fill
        extra += toCSS(
          `${textFields.map((v) => `${className}.ft--theme--FormField--${v} .ft--theme--FormField--${key} svg.ionicon`).join(',')}`,
          _.reduce(ioniconStyles, (r, v, k) => [...r, `${kebabCase(k)}: ${v};`], []).join(''),
          styles
        )
      }
    }
  })

  let height = transformCSSPositionValue(
    _.get(style, 'root.height', className ? 'auto' : ''),
    _.get(style, 'root.heightUnit', 'px'),
    false
  )
  let width = transformCSSPositionValue(
    _.get(style, 'root.width', className ? '' : ''),
    _.get(style, 'root.widthUnit', 'px'),
    true
  )
  let filled = _.get(style, 'filled', '')
  let cssWidth = ''
  if (width) {
    cssWidth += `${toCSS(
      `${textFields.map((v) => `${className}.ft--theme--FormField--${v}.ft--theme--FormField-container`).join(',')}`,
      `width: ${width};`,
      width
    )}`
  }
  if (height) {
    let isNewLogic = false
    try {
      if (textFields.includes('select')) {
        if (style.version) {
          isNewLogic = true
        }
        if (
          String(window?.location?.href).includes('ftnocache') ||
          String(window?.location?.href).includes('/project/')
        ) {
          isNewLogic = true
        }
      }
    } catch (err) {
      console.error(err)
    }
    if (isNewLogic && height != 'auto' && height != 'unset') {
      cssWidth += `${toCSS(`${textFields.map((v) => `${className}.ft--theme--FormField--${v} .inputRoot`).join(',')}`, `min-height: ${height}; max-height: ${height};`, height)}`
      cssWidth += `${toCSS(`${textFields.map((v) => `${className}.ft--theme--FormField--${v} .inputRoot`).join(',')} textarea`, `max-height: ${height}; white-space: nowrap;`, height)}`
    } else {
      cssWidth += `${toCSS(`${textFields.map((v) => `${className}.ft--theme--FormField--${v} .inputRoot`).join(',')}`, `min-height: ${height};`, height)}`
    }
    cssWidth += `${toCSS(`${textFields.map((v) => `${className}.ft--theme--FormField--${v}.ft--theme--FormField-container.resizer-wrap.ftb-resizers.FormField > .ft--control--root > ${className}.ft--theme--FormField--${v}.ft--theme--FormField-container`).join(',')}`, `width: 100%;max-width: 100%;`, width)}`
    cssWidth += `${toCSS(`${textFields.map((v) => `${className}.ft--theme--FormField--${v}.ft--theme--FormField-container.resizer-wrap.ftb-resizers.FormField > .ft--control--root > .ft--form--elem--wrap > ${className}.ft--theme--FormField--${v}.ft--theme--FormField-container`).join(',')}`, `width: 100%;max-width: 100%;`, width)}`
  }

  let cssBorderRadius = ''
  let borderRadius = _.get(style, 'input.borderRadius', '')
  if (borderRadius) {
    if (/^[0-9]+$/.test(borderRadius)) {
      borderRadius = `${borderRadius}px`
    }
    cssBorderRadius = `${toCSS(`${textFields.map((v) => `${className}.ft--theme--FormField--${v}`).join(',')}`, `border-radius: ${borderRadius};`, borderRadius)}`
    cssBorderRadius += `${toCSS(`${textFields.map((v) => `${className} ${className.replace('root', 'fieldset')}`).join(',')}`, `border-radius: ${borderRadius};`, borderRadius)}`
  }

  let cssBorderColor = ''
  let borderColor = _.get(style, 'input.borderColor', '')
  if (borderColor) {
    cssBorderColor = `${toCSS(`${textFields.map((v) => `${className}.ft--theme--FormField--${v}`).join(',')}`, `border-color: ${borderColor};`, borderColor)}`
    cssBorderColor += `${toCSS(`${textFields.map((v) => `${className} ${className.replace('root', 'fieldset')}`).join(',')}`, `border-color: ${borderColor};`, borderColor)}`
  }

  let cssBorderWidth = ''
  let borderWidth = _.get(style, 'input.borderWidth', '')
  if (borderWidth) {
    if (/^[0-9]+$/.test(borderWidth)) {
      borderWidth = `${borderWidth}px`
    }
    cssBorderWidth = `${toCSS(`${textFields.map((v) => `${className}.ft--theme--FormField--${v}`).join(',')}`, `border-width: ${borderWidth};`, borderWidth)}`
    cssBorderWidth += `${toCSS(`${textFields.map((v) => `${className} ${className.replace('root', 'fieldset')}`).join(',')}`, `border-width: ${borderWidth};`, borderWidth)}`
  }

  if (textFields.includes('addressFinder') || textFields.includes('externalLookup')) {
    extra += `${toCSS(
      `${textFields.map((v) => `${className}.ft--theme--FormField--${v}.ft--theme--FormField--filled > div`).join(',')}`,
      `background-color: ${_.get(style, 'filled', className ? '' : '')};border-radius: ${borderRadius};`,
      _.get(style, 'filled', className ? '' : ''),
      borderRadius
    )}
        ${toCSS(
          textFields
            .map(
              (v) =>
                `${className}.ft--theme--FormField--${v} > div:before, ${className}.ft--theme--FormField--${v} > div:hover:before`
            )
            .join(','),
          `border-bottom: 1px solid ${_.get(style, 'primary', '')} !important;`,
          _.get(style, 'primary', '')
        )}
        ${toCSS(
          textFields
            .map((v) => `${className}.ft--theme--FormField--${v}.fterror:not(.disableUnderlineStatus) > div:before`)
            .join(','),
          `border-bottom: 1px solid ${_.get(style, 'error', '')}!important;`,
          _.get(style, 'error', '')
        )}
        ${toCSS(
          textFields
            .map((v) => `${className}.ft--theme--FormField--${v}.ftsuccess:not(.disableUnderlineStatus) > div:before`)
            .join(','),
          `border-bottom: 1px solid ${_.get(style, 'success', '')}!important;`,
          _.get(style, 'success', '')
        )}
        ${toCSS(
          textFields.map((v) => `${className}.ft--theme--FormField--${v} > div:after`).join(','),
          `border-bottom: 2px solid ${_.get(style, 'primary', '')}!important;`,
          _.get(style, 'primary', '')
        )}
        ${toCSS(
          textFields
            .map((v) => `${className}.ft--theme--FormField--${v}.fterror:not(.disableUnderlineStatus) > div:after`)
            .join(','),
          `border-bottom: 2px solid ${_.get(style, 'error', '')}!important;`,
          _.get(style, 'error', '')
        )}
        ${toCSS(
          textFields
            .map((v) => `${className}.ft--theme--FormField--${v}.ftsuccess:not(.disableUnderlineStatus) > div:after`)
            .join(','),
          `border-bottom: 2px solid ${_.get(style, 'success', '')}!important;`,
          _.get(style, 'success', '')
        )}
        ${toCSS(
          textFields
            .map(
              (v) =>
                `${className}.ft--theme--FormField--${v} > div > .ftnotchedOutline,${className}.ft--theme--FormField--${v} > div:hover > .ftnotchedOutline`
            )
            .join(','),
          `border-color: ${_.get(style, 'primary', '')} !important;border-radius: ${borderRadius};${renderBorderStyle(style, ['input'], '', '', true)}`,
          _.get(style, 'primary', ''),
          borderRadius,
          _.size(style?.input)
        )}
        ${toCSS(
          textFields
            .map((v) => `${className}.ft--theme--FormField--${v}.fterror:not(.disableUnderlineStatus) > div:before`)
            .join(','),
          `border-bottom: 1px solid ${_.get(style, 'error', '')}!important;`,
          _.get(style, 'error', '')
        )}
        ${toCSS(
          textFields
            .map((v) => `${className}.ft--theme--FormField--${v}.ftsuccess:not(.disableUnderlineStatus) > div:before`)
            .join(','),
          `border-bottom: 1px solid ${_.get(style, 'success', '')}!important;`,
          _.get(style, 'success', '')
        )}
        ${toCSS(
          textFields.map((v) => `${className}.ft--theme--FormField--${v} > div:after`).join(','),
          `border-bottom: 2px solid ${_.get(style, 'primary', '')}!important;`,
          _.get(style, 'primary', '')
        )}
        ${toCSS(
          textFields
            .map((v) => `${className}.ft--theme--FormField--${v}.fterror:not(.disableUnderlineStatus) > div:after`)
            .join(','),
          `border-bottom: 2px solid ${_.get(style, 'error', '')}!important;`,
          _.get(style, 'error', '')
        )}
        ${toCSS(
          textFields
            .map((v) => `${className}.ft--theme--FormField--${v}.ftsuccess:not(.disableUnderlineStatus) > div:after`)
            .join(','),
          `border-bottom: 2px solid ${_.get(style, 'success', '')}!important;`,
          _.get(style, 'success', '')
        )}
        ${toCSS(
          textFields
            .map(
              (v) =>
                `${className}.ft--theme--FormField--${v}.fterror:not(.disableUnderlineStatus) > div .ftnotchedOutline`
            )
            .join(','),
          `${textFields.includes('externalLookup') ? `border-color: ${_.get(style, 'error', '')}!important` : `border-color: ${_.get(style, 'error', '')}!important`} border-radius: ${borderRadius};`,
          _.get(style, 'error', '')
        )}
        ${toCSS(
          textFields
            .map(
              (v) =>
                `${className}.ft--theme--FormField--${v}.ftsuccess:not(.disableUnderlineStatus) > div .ftnotchedOutline`
            )
            .join(','),
          `${renderBorderStyle(style, ['input'], '', '', true)}; ${textFields.includes('externalLookup') ? `border-color: ${_.get(style, 'success', '')}!important;` : ''} border-radius: ${borderRadius};`,
          _.get(style, 'success', ''),
          borderRadius,
          !_.isEmpty(style?.input)
        )}`
  }
  let padding = ``
  if (textFields.includes('addressFinder')) {
    padding = toCSS(
      '',
      `padding-left: ${transformCSSPositionValue(_.get(style, 'input.paddingLeft', ''), _.get(style, 'input.paddingLeftUnit', 'px'), true)}!important;
            padding-right: ${transformCSSPositionValue(_.get(style, 'input.paddingRight', ''), _.get(style, 'input.paddingRightUnit', 'px'), true)}!important;
            padding-top: ${transformCSSPositionValue(_.get(style, 'input.paddingTop', ''), _.get(style, 'input.paddingTopUnit', 'px'), true)}!important;
            padding-bottom: ${transformCSSPositionValue(_.get(style, 'input.paddingBottom', ''), _.get(style, 'input.paddingBottomUnit', 'px'), true)} !important;`,
      _.get(style, 'input.paddingLeft', ''),
      _.get(style, 'input.paddingRight', ''),
      _.get(style, 'input.paddingTop', ''),
      _.get(style, 'input.paddingBottom', '')
    )
    padding = _.trim(padding, '{}')
  }

  if (textFields.includes('multiSelect')) {
    if (_.size(style.elem) || style.chipItem) {
      const styles = getStyle(style.elem)
      if (_.get(style, 'chipItem')) {
        styles.color = _.get(style, 'chipItem')
      }
      if (
        styles.color === _.get(styles, 'backgroundColor', styles.color) &&
        _.startsWith(_.toLower(styles.color), '#ffffff')
      ) {
        styles.color = '#000'
      }
      extra += toCSS(
        `${textFields.map((v) => `${className}.ft--theme--FormField--${v} .ft--theme--FormField--chip`).join(',')}`,
        getStyleObjCss(styles),
        true
      )
    }
    if (_.size(style.elemHover) || style.chipItemHover) {
      const styles = getStyle(style.chipItemHover)
      if (_.get(style, 'chipItemHover')) {
        styles.color = _.get(style, 'chipItemHover')
      }
      extra += toCSS(
        `${textFields.map((v) => `${className}.ft--theme--FormField--${v} .ft--theme--FormField--chip:hover`).join(',')}`,
        getStyleObjCss(styles),
        true
      )
    }
    if (_.size(style.elemActive) || style.chipItemActive) {
      const styles = getStyle(style.chipItemActive)
      if (_.get(style, 'chipItemActive')) {
        styles.color = _.get(style, 'chipItemActive')
      }
      extra += toCSS(
        `${textFields.map((v) => `${className}.ft--theme--FormField--${v} .ft--theme--FormField--chip:active`).join(',')}`,
        getStyleObjCss(styles),
        true
      )
    }
    // ${toCSS(
    //     `${textFields.map(v => `${className}.ft--theme--FormField--${v} .ft--theme--FormField--chip svg`).join(',')}`,
    //     `fill: ${_.get(style, 'chipIconColor', className ? '' : '#000')};`,
    //     textFields.indexOf('multiSelect') !== -1,
    //     _.get(style, 'chipIconColor', className ? '' : '#000'),
    // )}
  }
  let chipColor = _.get(style, 'textItem', className ? '' : '')
  if (_.get(style, 'chipItem')) {
    chipColor = _.get(style, 'chipItem')
  }
  if (chipColor === _.get(style?.elem, 'backgroundColor', chipColor) && _.startsWith(_.toLower(chipColor), '#ffffff')) {
    chipColor = '#000'
  }

  const placeholderStyles = style?.placeholder || {}
  const inputRootPaddingStyle = toCSS(
    `${textFields.map((v) => `${className}.ft--theme--FormField--${v} .inputRoot .ft-input`).join(',')}`,
    getStyleObjCss(getStyle(style?.inputPadding)),
    !_.isEmpty(style?.inputPadding)
  )

  return `${cssWidth}${cssBorderRadius}${cssBorderColor}${cssBorderWidth}
    ${renderMUIPrefixStyle(textFields, typeStyle, defaultStyle, className)}
    ${renderMUILabelFieldStyle(textFields, typeStyle, defaultStyle, className)}
    ${toCSS(
      `${textFields.map((v) => `${className}.ft--theme--FormField--${v}.ft--theme--FormField--picklist--option`).join(',')}`,
      `font-family: ${trimFont(_.get(style, 'inputList.fontFamily', className ? '' : ''))};
        font-size: ${_.get(style, 'inputList.fontSize', className ? '' : '')};
        font-weight: ${_.get(style, 'inputList.fontWeight', className ? '' : '')};
        color: ${_.get(style, 'textList', className ? '' : '')};`,
      _.get(style, 'inputList.fontFamily', className ? '' : ''),
      _.get(style, 'inputList.fontSize', className ? '' : ''),
      _.get(style, 'inputList.fontWeight', className ? '' : ''),
      _.get(style, 'textList', className ? '' : '')
    )}
    ${toCSS(
      `${textFields.map((v) => `${className}.ft--theme--FormField--${v}.ft--theme--FormField--picklist--option:hover`).join(',')}`,
      `font-family: ${trimFont(_.get(style, 'inputListHover.fontFamily', className ? '' : ''))};
        font-size: ${_.get(style, 'inputListHover.fontSize', className ? '' : '')};
        font-weight: ${_.get(style, 'inputListHover.fontWeight', className ? '' : '')};
        color: ${_.get(style, 'textListHover', className ? '' : '')};`,
      _.get(style, 'inputListHover.fontFamily', className ? '' : ''),
      _.get(style, 'inputListHover.fontSize', className ? '' : ''),
      _.get(style, 'inputListHover.fontWeight', className ? '' : ''),
      _.get(style, 'textListHover', className ? '' : '')
    )}
    ${toCSS(
      `${textFields.map((v) => `${className}.ft--theme--FormField--${v}.ft--theme--FormField--picklist--option:active`).join(',')}`,
      `font-family: ${trimFont(_.get(style, 'inputListActive.fontFamily', className ? '' : ''))};
        font-size: ${_.get(style, 'inputListActive.fontSize', className ? '' : '')};
        font-weight: ${_.get(style, 'inputListActive.fontWeight', className ? '' : '')};
        color: ${_.get(style, 'textListActive', className ? '' : '')};`,
      _.get(style, 'inputListActive.fontFamily', className ? '' : ''),
      _.get(style, 'inputListActive.fontSize', className ? '' : ''),
      _.get(style, 'inputListActive.fontWeight', className ? '' : ''),
      _.get(style, 'textListActive', className ? '' : '')
    )}
    ${toCSS(
      `${textFields.map((v) => `${className}.ft--theme--FormField--${v} input,${className}.ft--theme--FormField--${v}.ft--theme--FormField--aria-select .ft-input, ${className}.ft--theme--FormField--${v} textarea, ${className}.ft--theme--FormField--${v} [role="textbox"]`).join(',')}`,
      `font-family: ${trimFont(_.get(style, 'input.fontFamily', className ? '' : ''))};
        font-size: ${_.get(style, 'input.fontSize', className ? '' : '')};
        font-weight: ${_.get(style, 'input.fontWeight', className ? '' : '')};
        color: var(--color, ${_.get(style, 'text', className ? '' : '')});
        ${height ? `height: ${height};box-sizing: border-box;` : ''};z-index: 1;
        ${padding}
        ${_.get(style, 'root.customCSS', '')};`,
      _.get(style, 'input.fontFamily', className ? '' : ''),
      _.get(style, 'input.fontSize', className ? '' : ''),
      _.get(style, 'input.fontWeight', className ? '' : ''),
      _.get(style, 'text', className ? '' : ''),
      _.get(style, 'root.customCSS', ''),
      height,
      padding
    )}
    ${toCSS(
      `${textFields.map((v) => `${className}.ft--theme--FormField--${v}.ft--theme--FormField--aria-select .ft-input`).join(',')}`,
      `${height ? `height: auto;box-sizing: border-box;` : ''};`,
      height
    )}
    ${toCSS(
      `${textFields.map((v) => `${className}.ft--theme--FormField--filled.ft--theme--FormField--${v} .inputRoot, ${className}.ft--theme--FormField--outlined.ft--theme--FormField--${v} .inputRoot`).join(',')}`,
      `height: ${height}; padding: 0 12px 0px !important;${textFields.includes('multiSelect') ? 'row-gap: 6px' : ''};`,
      height,
      true
    )}
    ${toCSS(
      `${textFields.map((v) => `${className}.ft--theme--FormField--filled.ft--theme--FormField--multiSelect .inputRoot`).join(',')}`,
      `height: ${height}; padding: 19px 12px 0px !important;${textFields.includes('multiSelect') ? 'row-gap: 6px' : ''};`,
      height,
      textFields.includes('multiSelect')
    )}
    ${toCSS(
      `${textFields.map((v) => `${className}.ft--theme--FormField--ftclassic.ft--theme--FormField--select .inputRoot input, ${className}.ft--theme--FormField--ftoutined.ft--theme--FormField--select .inputRoot input`).join(',')}`,
      `padding-left: 14px !important;padding-right: 14px !important;${padding}`,
      height,
      textFields.includes('multiSelect'),
      padding
    )}
    ${toCSS(
      `${textFields.map((v) => `${className}.ft--theme--FormField--filled.ft--theme--FormField--multiSelect .inputRoot input`).join(',')}`,
      `padding-top: 17px !important;padding-bottom: 20px !important;${padding}`,
      height,
      textFields.includes('multiSelect'),
      padding
    )}
    ${toCSS(
      `${textFields.map((v) => `${className}.fix-label-padding-small.ft--theme--FormField--filled.ft--theme--FormField--multiSelect .inputRoot input`).join(',')}`,
      `padding-top: 32px !important;padding-bottom: 16px !important;${padding}`,
      height,
      textFields.includes('multiSelect'),
      padding
    )}
    ${toCSS(
      `${textFields.map((v) => `${className}.ft--theme--FormField--filled.ft--theme--FormField--${v}.fix-label-padding-small .inputRoot, ${className}.ft--theme--FormField--outlined.ft--theme--FormField--${v} .inputRoot`).join(',')}`,
      `height: ${height}; padding: 0px 12px 0px 12px !important;row-gap: 6px;`,
      height,
      true
    )}
    ${toCSS(
      `${textFields.map((v) => `${className}.ft--theme--FormField--ftclassic.ft--theme--FormField--size-medium.ft--theme--FormField--filled.ft--theme--FormField--${v}.fix-label-padding-small .inputRoot, ${className}.ft--theme--FormField--outlined.ft--theme--FormField--${v} .inputRoot`).join(',')}`,
      `height: ${height}; padding: 0px !important;row-gap: 6px;`,
      height,
      true
    )}
    ${toCSS(
      `${textFields.map((v) => `${className}.ft--theme--FormField--ftclassic.ft--theme--FormField--size-medium.ft--theme--FormField--filled.ft--theme--FormField--${v}.fix-label-padding-small .inputRoot input, ${className}.ft--theme--FormField--outlined.ft--theme--FormField--${v} .inputRoot input`).join(',')}`,
      `padding-top: 8px;padding-bottom: 8px;${padding}`,
      true
    )}
    ${toCSS(
      `${textFields.map((v) => `${className}.ft--theme--FormField--outlined.ft--theme--FormField--multiSelect .inputRoot`).join(',')}`,
      `padding: 11px 12px !important;`,
      height,
      textFields.includes('multiSelect')
    )}
    ${toCSS(
      `${textFields.map((v) => `${className}.ft--theme--FormField--standard.ft--theme--FormField--${v} .inputRoot`).join(',')}`,
      `height: auto;`,
      true
    )}
    ${toCSS(
      `${textFields.map((v) => `${className}.ft--theme--FormField--standard.ft--theme--FormField--${v} textarea`).join(',')}`,
      `padding: 0px 0 6px !important;line-height: normal;box-sizing: content-box;`,
      true
    )}
    ${toCSS(
      `${textFields.map((v) => `${className}.ft--theme--FormField--standard.ft--theme--FormField--${v} input`).join(',')}`,
      `padding: 6px 0 7px !important;line-height: normal;box-sizing: content-box;${padding}`,
      true
    )}
    ${toCSS(
      `${textFields.map((v) => `${className}.fix-label-padding-small.ft--theme--FormField--standard.ft--theme--FormField--${v} input`).join(',')}`,
      `padding: 2px 0 7px !important;line-height: normal;box-sizing: content-box;${padding}`,
      true
    )}
    ${toCSS(
      `${textFields.map((v) => `${className}.ft--theme--FormField--fixed.ft--theme--FormField--${v} .inputRoot`).join(',')}`,
      `height: auto;`,
      true
    )}
    ${toCSS(
      `${textFields.map((v) => `${className}.ft--theme--FormField--fixed.ft--theme--FormField--${v} .inputRoot`).join(',')}`,
      `height: auto;padding: 0`,
      height !== 'auto' && height !== 'unset'
    )}
    ${toCSS(
      `${textFields.map((v) => `${className}.ft--theme--FormField--fixed.ft--theme--FormField--${v} textarea, ${className}.ft--theme--FormField--fixed.ft--theme--FormField--${v} input`).join(',')}`,
      `padding: 6px 0 7px 0px !important;line-height: normal;box-sizing: content-box;${padding}`,
      true
    )}
    ${toCSS(
      `${textFields.map((v) => `${className}.ft--theme--FormField--fixed.ft--theme--FormField--${v}.ft--theme--FormField--size-medium textarea`).join(',')}`,
      `padding: 1px 0 5px 0px !important;${padding}`,
      textFields.includes('select')
    )}
    ${toCSS(
      `${textFields.map((v) => `${className}.ft--theme--FormField--fixed.ft--theme--FormField--${v} textarea, ${className}.ft--theme--FormField--fixed.ft--theme--FormField--${v} input`).join(',')}`,
      `padding: 6px 0 7px 0px !important;line-height: normal;box-sizing: border-box;${padding}`,
      height !== 'auto' && height !== 'unset'
    )}
    ${toCSS(
      `${textFields.map((v) => `${className}.ft--theme--FormField--fixed.ft--theme--FormField--${v} textarea`).join(',')}`,
      `padding: 0 !important;`,
      height !== 'auto' && height !== 'unset'
    )}
    ${toCSS(
      `${textFields.map((v) => `${className}.ft--theme--FormField--filled.ft--theme--FormField--${v} textarea, ${className}.ft--theme--FormField--filled.ft--theme--FormField--${v} [role="textbox"], ${className}.ft--theme--FormField--outlined.ft--theme--FormField--${v} textarea, ${className}.ft--theme--FormField--outlined.ft--theme--FormField--${v} [role="textbox"]`).join(',')}`,
      `height: ${height}; padding-left: 14px; padding-right: 14px;padding-left: 12px;`,
      height
    )}
    ${toCSS(
      `${textFields.map((v) => `${className}.ft--theme--FormField--filled.ft--theme--FormField--${v} input, ${className}.ft--theme--FormField--filled.ft--theme--FormField--${v} [role="textbox"]`).join(',')}`,
      `padding-left: 0px;${padding}`,
      true
    )}
    ${toCSS(
      `${textFields.map((v) => `${className}.ft--theme--FormField--${v} .ft--theme--FormField--chip`).join(',')}`,
      `font-family: ${trimFont(_.get(style, 'inputItem.fontFamily', className ? '' : ''))};
        font-size: ${_.get(style, 'inputItem.fontSize', className ? '' : '')};
        font-weight: ${_.get(style, 'inputItem.fontWeight', className ? '' : '')};
        color: ${chipColor};`,
      _.get(style, 'inputItem.fontFamily', className ? '' : ''),
      _.get(style, 'inputItem.fontSize', className ? '' : ''),
      _.get(style, 'inputItem.fontWeight', className ? '' : ''),
      chipColor
    )}
    ${inputRootPaddingStyle}
    ${toCSS(
      `${textFields.map((v) => `${className}.ft--theme--FormField--${v} input::placeholder`).join(',')}`,
      `opacity: 1;${getStyleObjCss(placeholderStyles)}`,
      _.size(placeholderStyles)
    )}
    ${toCSS(
      `${textFields.map((v) => `${className}.ft--theme--FormField--${v} textarea::placeholder`).join(',')}`,
      `opacity: 1;${getStyleObjCss(placeholderStyles)}`,
      _.size(placeholderStyles)
    )}
    ${toCSS(
      `${textFields.map((v) => `${className}.ft--theme--FormField--${v} .ft--theme--FormField--chip svg`).join(',')}`,
      `fill: ${_.get(style, 'chipIconColor', className ? '' : '#000')};`,
      textFields.indexOf('multiSelect') !== -1,
      _.get(style, 'chipIconColor', className ? '' : '#000')
    )}
    ${toCSS(
      `${textFields.map((v) => `${className}.ft--theme--FormField--${v}.ft--theme--FormField--filled`).join(',')}`,
      `background: none;`,
      textFields.indexOf('select') !== -1 || textFields.indexOf('multiSelect') !== -1
    )}
    ${toCSS(
      `${textFields.map((v) => `${className}.ft--theme--FormField--${v}.ft--theme--FormField--filled > div > div`).join(',')}`,
      `background-color: ${_.get(style, 'filled', className ? '' : '')};`,
      _.get(style, 'filled', className ? '' : '')
    )}
    ${toCSS(
      textFields
        .map(
          (v) =>
            `${className}.ft--theme--FormField--${v} > div > div:before, ${className}.ft--theme--FormField--${v} > div > div:hover:before`
        )
        .join(','),
      `border-bottom: 1px solid ${_.get(style, 'primary', '')} !important;`,
      _.get(style, 'primary', '')
    )}
    ${toCSS(
      textFields
        .map(
          (v) =>
            `${className}.ft--theme--FormField--${v} .inputRoot:before, ${className}.ft--theme--FormField--${v} .inputRoot:hover:before`
        )
        .join(','),
      `border-bottom: 1px solid ${_.get(style, 'primary', '')} !important;`,
      _.get(style, 'primary', '')
    )}
    ${toCSS(
      textFields
        .map((v) => `${className}.ft--theme--FormField--${v}.fterror:not(.disableUnderlineStatus) > div > div:before`)
        .join(','),
      `border-bottom: 1px solid ${_.get(style, 'error', '')}!important;`,
      _.get(style, 'error', '')
    )}
    ${toCSS(
      textFields
        .map((v) => `${className}.ft--theme--FormField--${v}.fterror:not(.disableUnderlineStatus) .inputRoot:before`)
        .join(','),
      `border-bottom: 1px solid ${_.get(style, 'error', '')}!important;`,
      _.get(style, 'error', '')
    )}
    ${toCSS(
      textFields
        .map((v) => `${className}.ft--theme--FormField--${v}.ftsuccess:not(.disableUnderlineStatus) > div > div:before`)
        .join(','),
      `border-bottom: 1px solid ${_.get(style, 'success', '')}!important;`,
      _.get(style, 'success', '')
    )}
    ${toCSS(
      textFields
        .map((v) => `${className}.ft--theme--FormField--${v}.ftsuccess:not(.disableUnderlineStatus) .inputRoot:before`)
        .join(','),
      `border-bottom: 1px solid ${_.get(style, 'success', '')}!important;`,
      _.get(style, 'success', '')
    )}
    ${toCSS(
      textFields.map((v) => `${className}.ft--theme--FormField--${v} > div > div:after`).join(','),
      `border-bottom: 2px solid ${_.get(style, 'primary', '')}!important;`,
      _.get(style, 'primary', '')
    )}
    ${toCSS(
      textFields.map((v) => `${className}.ft--theme--FormField--${v} .inputRoot:after`).join(','),
      `border-bottom: 2px solid ${_.get(style, 'primary', '')}!important;`,
      _.get(style, 'primary', '')
    )}
    ${toCSS(
      textFields
        .map((v) => `${className}.ft--theme--FormField--${v}.fterror:not(.disableUnderlineStatus) > div > div:after`)
        .join(','),
      `border-bottom: 2px solid ${_.get(style, 'error', '')}!important;`,
      _.get(style, 'error', '')
    )}
    ${toCSS(
      textFields
        .map((v) => `${className}.ft--theme--FormField--${v}.fterror:not(.disableUnderlineStatus) .inputRoot:after`)
        .join(','),
      `border-bottom: 2px solid ${_.get(style, 'error', '')}!important;`,
      _.get(style, 'error', '')
    )}
    ${toCSS(
      textFields
        .map((v) => `${className}.ft--theme--FormField--${v}.ftsuccess:not(.disableUnderlineStatus) > div > div:after`)
        .join(','),
      `border-bottom: 2px solid ${_.get(style, 'success', '')}!important;`,
      _.get(style, 'success', '')
    )}
    ${toCSS(
      textFields
        .map((v) => `${className}.ft--theme--FormField--${v}.ftsuccess:not(.disableUnderlineStatus) .inputRoot:after`)
        .join(','),
      `border-bottom: 2px solid ${_.get(style, 'success', '')}!important;`,
      _.get(style, 'success', '')
    )}
    ${toCSS(
      textFields
        .map(
          (v) =>
            `${className}.ft--theme--FormField--${v}.ft--theme--FormField--aria-select fieldset,${className}.ft--theme--FormField--${v}.ft--theme--FormField--aria-select:hover fieldset`
        )
        .join(','),
      `border-color: ${_.get(style, 'primary', '')} !important; border-radius: ${borderRadius};${renderBorderStyle(style, ['input'], '', '', true)}`,
      _.get(style, 'primary', '')
    )}
    ${toCSS(
      textFields
        .map(
          (v) =>
            `${className}.ft--theme--FormField--${v} > div > div .ftnotchedOutline,${className}.ft--theme--FormField--${v} > div:hover .ftnotchedOutline`
        )
        .join(','),
      `border-color: ${_.get(style, 'primary', '')} !important; border-radius: ${borderRadius};${renderBorderStyle(style, ['input'], '', '', true)}`,
      _.get(style, 'primary', '')
    )}
    ${toCSS(
      textFields
        .map(
          (v) =>
            `${className}.ft--theme--FormField--${v} > div > div .ftnotchedOutline-classic,${className}.ft--theme--FormField--${v} div:hover .ftnotchedOutline-classic`
        )
        .join(','),
      `background-color: var(--bg-color, ${filled}) !important;z-index: 0;top: -2px; border-radius: ${borderRadius};${renderBorderStyle(style, ['input'], '', '', true)}`,
      filled
    )}
${toCSS(
  textFields
    .map((v) => `${className}.ft--theme--FormField--${v}.ft--theme--FormField--ftclassic > div > div .ftnotchedOutline`)
    .join(','),
  `max-height: calc(100% + 3px);`,
  textFields.includes('select')
)}
${toCSS(
  textFields
    .map(
      (v) =>
        `${className}.ft--theme--FormField--${v}.fterror:not(.disableUnderlineStatus) > div > div .ftnotchedOutline`
    )
    .join(','),
  ` border-color: ${_.get(style, 'error', '')}!important; border-radius: ${borderRadius};`,
  _.get(style, 'error', '')
)}
${toCSS(
  textFields
    .map(
      (v) =>
        `${className}.ft--theme--FormField--${v}.ft--theme--FormField--aria-select.fterror:not(.disableUnderlineStatus) fieldset`
    )
    .join(','),
  ` border-color: ${_.get(style, 'error', '')}!important; border-radius: ${borderRadius};`,
  _.get(style, 'error', '')
)}
${toCSS(
  textFields
    .map(
      (v) =>
        `${className}.ft--theme--FormField--${v}.ftsuccess:not(.disableUnderlineStatus) > div > div .ftnotchedOutline`
    )
    .join(','),
  `${renderBorderStyle(style, ['input'], '', '', true)};border-color: ${_.get(style, 'success', '')}!important; border-radius: ${borderRadius};`,
  _.get(style, 'success', ''),
  borderRadius,
  !_.isEmpty(style?.input)
)}
${toCSS(
  textFields
    .map(
      (v) =>
        `${className}.ft--theme--FormField--${v}.ft--theme--FormField--aria-select.ftsuccess:not(.disableUnderlineStatus) fieldset`
    )
    .join(','),
  `${renderBorderStyle(style, ['input'], '', '', true)};border-color: ${_.get(style, 'success', '')}!important; border-radius: ${borderRadius};`,
  _.get(style, 'success', ''),
  borderRadius,
  !_.isEmpty(style?.input)
)}
    ${extra}`
}
const renderMUITextFieldStyle = (textFields, typeStyle, defaultStyle, className) => {
  const style = mergeDeep({}, deepClean(defaultStyle), deepClean(typeStyle))
  if (textFields[0] === 'date') {
    if (_.findKey(typeStyle?.input || {}, (v, k) => k.indexOf('border') !== -1)) {
      delete style.success
    }
  }

  let extra = ''
  let height = transformCSSPositionValue(
    _.get(style, 'root.height', className && !textFields.includes('fileupload') ? 'auto' : ''),
    _.get(style, 'root.heightUnit', 'px'),
    false || textFields.includes('fileupload')
  )
  let borderRadius = _.get(style, 'input.borderRadius', '')
  if (borderRadius) {
    if (/^[0-9]+$/.test(borderRadius)) {
      borderRadius = `${borderRadius}px`
    }
  }
  if (textFields.includes('date') || textFields.includes('time') || textFields.includes('datetime')) {
    let headerStyle = getStyle(_.get(style, 'header', {}))
    let selectionStyle = getStyle(_.get(style, 'selection', {}))
    let btnStyle = getStyle(_.get(style, 'modal-btn', {}))

    if (_.get(style, ['header', '--ft-border'])) {
      headerStyle = _.omit(headerStyle, ['borderColor', 'borderStyle', 'borderWidth'])
    } else {
      headerStyle = _.omit(
        headerStyle,
        _.flatMap(['Top', 'Left', 'Right', 'Bottom'], (key) => [
          `border${key}Color`,
          `border${key}Style`,
          `border${key}Width`,
        ])
      )
    }

    if (_.size(headerStyle) || _.size(selectionStyle) || _.size(btnStyle)) {
      extra += toCSS(
        `${textFields.map((v) => `${className}.ft--theme--FormField--${v}.ft--theme--FormField--modal`).join(',')}`,
        `--mui-primary:${_.get(style, 'primary', '') ? _.get(style, 'primary', '') : '#0091FF'};
                ${_.map(headerStyle, (v, k) => `--mui-toolbar-${kebabCase(k)}: ${v};`).join('')}
                ${_.map(selectionStyle, (v, k) => `--mui-sel-${kebabCase(k)}: ${v};`).join('')}`,
        true
      )
      extra += toCSS(
        `${textFields.map((v) => `${className}.ft--theme--FormField--${v}.ft--theme--FormField--modal > div:nth-child(2) button`).join(',')}`,
        _.map(btnStyle, (v, k) => `${kebabCase(k)}: ${v};`).join(''),
        true
      )
    } else {
      extra += toCSS(
        `${textFields.map((v) => `${className}.ft--theme--FormField--${v}.ft--theme--FormField--modal`).join(',')}`,
        `--mui-primary:${_.get(style, 'primary', '') ? _.get(style, 'primary', '') : '#0091FF'};`,
        true
      )
    }
  }
  if (textFields.includes('search')) {
    extra += toCSS(
      `${className} .ft--theme--FormField--search-ico`,
      `color: ${_.get(style, 'searchButton.searchIconColor') || _.get(style, 'primary', '')};
            padding-top: ${_.get(style, 'searchButton.paddingTop', '')}px;
            padding-right: ${_.get(style, 'searchButton.paddingRight', '')}px;
            padding-bottom: ${_.get(style, 'searchButton.paddingBottom', '')}px;
            padding-left: ${_.get(style, 'searchButton.paddingLeft', '')}px;
            border-color: ${_.get(style, 'searchButton.borderColor', 'black')};
            border-radius: ${_.get(style, 'searchButton.borderRadius', '0')}px;
            border-width: ${_.get(style, 'searchButton.borderWidth', '0')}px;;
            border-style: ${_.get(style, 'searchButton.borderStyle', 'solid')};`,
      _.get(style, 'searchButton', className) || _.get(style, 'primary', '')
    )
    extra += toCSS(
      `${className}.ft--theme--FormField--search .ft--theme--FormField--searchButton`,
      _.reduce(getStyle(style.searchButton), (r, v, k) => [...r, `${kebabCase(k)}: ${v};`], []).join(''),
      !_.isEmpty(style.searchButton)
    )
    extra += toCSS(
      `${className}.ft--theme--FormField--search .ft--theme--FormField--searchButton:hover`,
      _.reduce(getStyle(style.searchButtonHover), (r, v, k) => [...r, `${kebabCase(k)}: ${v};`], []).join(''),
      !_.isEmpty(style.searchButtonHover)
    )
    extra += toCSS(
      `${className}.ft--theme--FormField--search .ft--theme--FormField--searchButton:active`,
      _.reduce(getStyle(style.searchButtonActive), (r, v, k) => [...r, `${kebabCase(k)}: ${v};`], []).join(''),
      !_.isEmpty(style.searchButtonActive)
    )

    extra += toCSS(
      `${className}.ft--theme--FormField--search .ft--theme--FormField--clear`,
      _.reduce(getStyle(style.clear), (r, v, k) => [...r, `${kebabCase(k)}: ${v};`], []).join(''),
      !_.isEmpty(style.clear)
    )

    extra += toCSS(
      `${className}.ft--theme--FormField--search .ft--theme--FormField--ico`,
      _.reduce(getStyle(style.ico), (r, v, k) => [...r, `${kebabCase(k)}: ${v};`], []).join(''),
      !_.isEmpty(style.ico)
    )

    if (style?.btnIcon?.fill)
      // Mui icon fills are set by css - currentColor
      style.btnIcon.color = style.btnIcon.fill

    const sizeOmitted = _.omit(style.btnIcon, ['width', 'height'])
    const styleOmitted = _.reduce(getStyle(sizeOmitted), (r, v, k) => [...r, `${kebabCase(k)}: ${v};`], []).join('')
    const sizePicked = _.pick(style.btnIcon, ['width', 'height'])
    const stylePicked = _.reduce(getStyle(sizePicked, true), (r, v, k) => [...r, `${kebabCase(k)}: ${v};`], []).join('')

    extra += toCSS(
      `${className}.ft--theme--FormField--search .ft--theme--FormField--btnIcon`,
      styleOmitted + stylePicked,
      !_.isEmpty(style.btnIcon)
    )

    if (!_.isEmpty(style.input)) {
      extra += toCSS(
        `${className}.ft--theme--FormField--search .ft--theme--FormField--searchInput`,
        `padding-top: ${_.get(style, 'input.paddingTop', '')}px;
                padding-right: ${_.get(style, 'input.paddingRight', '')}px;
                padding-bottom: ${_.get(style, 'input.paddingBottom', '')}px;
                padding-left: ${_.get(style, 'input.paddingLeft', '')}px;
                border-color: ${_.get(style, 'input.borderColor', 'black')};
                border-radius: ${_.get(style, 'input.borderRadius', '0')}px;
                border-width: ${_.get(style, 'input.borderWidth', '0')}px;;
                border-style: ${_.get(style, 'item.borderStyle', 'solid')};`,
        _.get(style, 'input', className) || _.get(style, 'primary', '')
      )
    }
  }
  _.each(['validIcon', 'invalidIcon'], (key) => {
    if (!_.isEmpty(style[key])) {
      let styles = getStyle(style[key])
      extra += toCSS(
        `${textFields.map((v) => `${className}.ft--theme--FormField--${v} .ft--theme--FormField--${key} svg`).join(',')}`,
        _.reduce(styles, (r, v, k) => [...r, `${kebabCase(k)}: ${v};`], []).join(''),
        styles
      )
      let ioniconStyles = getStyle(style[key])
      if (ioniconStyles.fill) {
        ioniconStyles.color = ioniconStyles.fill
        extra += toCSS(
          `${textFields.map((v) => `${className}.ft--theme--FormField--${v} .ft--theme--FormField--${key} svg.ionicon`).join(',')}`,
          _.reduce(ioniconStyles, (r, v, k) => [...r, `${kebabCase(k)}: ${v};`], []).join(''),
          styles
        )
      }
    }
  })
  _.each(['viewFiles', 'viewFilesHover', 'viewFilesActive'], (key) => {
    if (!_.isEmpty(style[key])) {
      let styles = getStyle(style[key])
      let prefix = ''
      if (_.endsWith(key, 'Hover')) {
        prefix = ':hover'
      } else if (_.endsWith(key, 'Active')) {
        prefix = ':active'
      }
      extra += toCSS(
        `${textFields.map((v) => `${className}.ft--theme--FormField--${v} .ft-view-files-btn${prefix}`).join(',')}`,
        _.reduce(styles, (r, v, k) => [...r, `${kebabCase(k)}: ${v};`], []).join(''),
        styles
      )
    }
  })
  if (textFields.indexOf('lookup') !== -1) {
    if (style.lookupIconColor) {
      extra += toCSS(
        `${className}.ft--theme--FormField--lookup svg`,
        `color: ${_.get(style, 'lookupIconColor', className ? '' : '')};`,
        _.get(style, 'lookupIconColor', className ? '' : '')
      )
    }
  }
  if (textFields.indexOf('signature') !== -1) {
    extra += toCSS(
      `${className} .ft--theme--FormField--canvas`,
      `border-color: ${_.get(style, 'borderColor', 'black')};
            border-radius: ${_.get(style, 'borderRadius', '0')}px;
            border-width: ${_.get(style, 'borderWidth', '1')}px;${height ? `height: ${height} !important;box-sizing: border-box;` : ''}
            border-radius: ${borderRadius};${renderBorderStyle(style, ['input'])}
            `,
      _.get(style, 'borderColor', 'black'),
      _.get(style, 'borderRadius', '0'),
      _.get(style, 'borderWidth', '1px'),
      height,
      borderRadius,
      _.size(style?.input)
    )
  }
  if (textFields.includes('richtextarea')) {
    let borderRadius = _.get(style, 'input.borderRadius', '')
    if (borderRadius) {
      if (/^[0-9]+$/.test(borderRadius)) {
        borderRadius = `${borderRadius}px`
      }
    }
    extra += toCSS(
      textFields.map((v) => `${className}.ft--theme--FormField--${v} .mce-container[role="application"]`).join(','),
      `border-radius: ${borderRadius};${renderBorderStyle(style, ['input'], '', '', true)}overflow: hidden;`,
      borderRadius,
      _.get(style, 'input', {})
    )
  }
  if (
    textFields.includes('date') ||
    textFields.includes('time') ||
    textFields.includes('datetime') ||
    textFields.includes('daterange')
  ) {
    if (style.datetimeIconColor || style.datetimeIconWidth) {
      extra += toCSS(
        [textFields].map((v) => `${className}.ft--theme--FormField--${v} svg`).join(','),
        `color: ${_.get(style, 'datetimeIconColor', className ? '' : '')};
                width: ${_.get(style, 'datetimeIconWidth', '')}px !important;
                height: ${_.get(style, 'datetimeIconWidth', '')}px !important;`,
        _.get(style, 'datetimeIconColor', className ? '' : ''),
        _.get(style, 'datetimeIconWidth', className ? '' : '')
      )
    }
    if (style.clearIconColor || style.clearIconWidth) {
      extra += toCSS(
        [textFields].map((v) => `${className}.ft--theme--FormField--${v} svg.ftclear`).join(','),
        `color: ${_.get(style, 'clearIconColor', className ? '' : '')} !important;
                width: ${_.get(style, 'clearIconWidth', '')}px !important;
                height: ${_.get(style, 'clearIconWidth', '')}px !important;`,
        _.get(style, 'clearIconColor', className ? '' : ''),
        _.get(style, 'clearIconWidth', className ? '' : '')
      )
    }
    if (!_.isEmpty(style.dateIcon)) {
      let styles = getStyle(style.dateIcon)
      styles.color = styles.fill
      extra += toCSS(
        [textFields].map((v) => `${className}.ft--theme--FormField--${v} svg`).join(','),
        getStyleObjCss(styles),
        true
      )
    }
  }
  if (
    textFields.indexOf('text') !== -1 ||
    textFields.indexOf('textarea') !== -1 ||
    textFields.indexOf('richtextarea') !== -1
  ) {
    extra += toCSS(
      `${className}.chars-counter`,
      `font-family: ${_.get(style, 'char.fontFamily', '')};
            font-size: ${_.get(style, 'char.fontSize', '14px')};
            color: ${_.get(style, 'char.color', '#000')};
            font-weight: ${_.get(style, 'char.fontWeight', '')};
            --ft--primary--color: ${_.get(style, 'char.color', _.get(style, 'char.color', '#000'))};
            height: auto;
            min-height: auto;
            max-height: initial;`,
      true
    )
  }
  if (textFields.includes('phone')) {
    if (style.phoneIconColor || style.phone?.width || style.phone?.height) {
      extra += toCSS(
        [textFields].map((v) => `${className}.ft--theme--FormField--${v} .PhoneInputCountryIcon svg`).join(','),
        `color: ${_.get(style, 'phoneIconColor', className ? '' : '')};
                width: ${style.phone?.width}px !important;
                height: ${style.phone?.height}px !important;`,
        _.get(style, 'phoneIconColor', className ? '' : ''),
        style.phone?.width,
        style.phone?.height
      )
    }
  }
  if (textFields.includes('counter')) {
    _.each(['decBtn', 'incBtn'], (key) => {
      if (style[key]) {
        let styles = getStyle(style[key])
        extra += toCSS(
          `${textFields.map((v) => `${className}.ft--theme--FormField--${v} button.ft--theme--FormField--${key}`).join(',')}`,
          _.reduce(styles, (r, v, k) => [...r, `${kebabCase(k)}: ${v};`], []).join(''),
          styles
        )
      }
      if (style[`${key}Hover`]) {
        let styles = getStyle(style[`${key}Hover`])
        extra += toCSS(
          `${textFields.map((v) => `${className}.ft--theme--FormField--${v} button.ft--theme--FormField--${key}:hover`).join(',')}`,
          _.reduce(styles, (r, v, k) => [...r, `${kebabCase(k)}: ${v};`], []).join(''),
          styles
        )
      }
      if (style[`${key}Active`]) {
        let styles = getStyle(style[`${key}Active`])
        extra += toCSS(
          `${textFields.map((v) => `${className}.ft--theme--FormField--${v} button.ft--theme--FormField--${key}:active`).join(',')}`,
          _.reduce(styles, (r, v, k) => [...r, `${kebabCase(k)}: ${v};`], []).join(''),
          styles
        )
      }
    })
    _.each(['decIcon', 'incIcon'], (key) => {
      if (style[key]) {
        let styles = getStyle(style[key])
        if (styles.fill) {
          styles.color = styles.fill
        }
        extra += toCSS(
          `${textFields.map((v) => `${className}.ft--theme--FormField--${v} .ft--theme--FormField--${key} svg`).join(',')}`,
          _.reduce(styles, (r, v, k) => [...r, `${kebabCase(k)}: ${v};`], []).join(''),
          styles
        )
      }
    })
  }
  let width = transformCSSPositionValue(
    _.get(style, 'root.width', className ? '' : ''),
    _.get(style, 'root.widthUnit', 'px'),
    true
  )
  let filled = _.get(style, 'filled', '')
  let cssWidth = ''
  if (width) {
    let extraClasses = ''
    if (textFields.includes('search') && String(className).includes('-root')) {
      extraClasses = '.resizer-wrap.ftb-resizers'
    }
    cssWidth = `${toCSS(`${textFields.map((v) => `${className}.ft--theme--FormField--${v}.ft--theme--FormField-container${extraClasses}`).join(',')}`, `width: ${width};`, width)}`
    cssWidth += `${toCSS(`${textFields.map((v) => `${className}.ft--theme--FormField--${v}.ft--theme--FormField-container.resizer-wrap.ftb-resizers.FormField > .ft--control--root > ${className}.ft--theme--FormField--${v}.ft--theme--FormField-container`).join(',')}`, `width: 100%;max-width: 100%;`, width)}`
    cssWidth += `${toCSS(`${textFields.map((v) => `${className}.ft--theme--FormField--${v}.ft--theme--FormField-container.resizer-wrap.ftb-resizers.FormField > .ft--control--root > .ft--form--elem--wrap > ${className}.ft--theme--FormField--${v}.ft--theme--FormField-container`).join(',')}`, `width: 100%;max-width: 100%;`, width)}`
  }
  let areaHeight = ''
  if (height && height != 'unset' && textFields.indexOf('textarea') > -1) {
    areaHeight = toCSS(
      `${className}.ft--theme--FormField--textarea .ft--theme--FormField--textarea-form-control`,
      `height: ${height};box-sizing: border-box;`,
      height
    )
  }

  let textAreaStyle = ''
  if (textFields.indexOf('textarea') > -1) {
    textAreaStyle = `${toCSS(
      `${className}.ft--theme--FormField--textarea textarea, ${className}.ft--theme--FormField--textarea [role="textbox"], ${className}.ft--theme--FormField--textarea textarea::placeholder`,
      `font-family: ${trimFont(_.get(style, 'input.fontFamily', className ? '' : ''))};
            font-size: ${_.get(style, 'input.fontSize', className ? '' : '')};
            font-weight: ${_.get(style, 'input.fontWeight', className ? '' : '')};
            color: ${_.get(style, 'text', className ? '' : '')};z-index: 1;
            ${_.get(style, 'root.customCSS', '')};
            `,
      _.get(style, 'text', className ? '' : ''),
      _.get(style, 'input.fontFamily', className ? '' : ''),
      _.get(style, 'input.fontSize', className ? '' : ''),
      _.get(style, 'input.fontWeight', className ? '' : ''),
      _.get(style, 'root.customCSS', ''),
      height
    )}`
  }

  const placeholderStyles = style?.placeholder || {}
  let inputShadow = renderShadowCss(style?.input)
  let inputPaddingStyle = ''
  let paddingKyes = ['Left', 'Right', 'Top', 'Bottom']
  _.each(paddingKyes, (key) => {
    if (_.get(style?.input, `padding${key}`, '')) {
      inputPaddingStyle += `padding-${key.toLowerCase()}: ${transformCSSPositionValue(_.get(style?.input, `padding${key}`, ''), _.get(style?.input, `padding${key}Unit`, 'px'), true)} !important;`
    }
  })
  if (borderRadius) {
    inputPaddingStyle += `border-radius: ${borderRadius};`
  }
  // textFields.includes('text') && console.log(
  //     '>>>',
  //     {textFields, typeStyle, defaultStyle, className},
  //     renderMUILabelFieldStyle(textFields, typeStyle, defaultStyle, className)
  // )
  const underlineColor = _.trim(_.get(style, 'input.--underline', ''))
  return `
    ${cssWidth}
    ${renderMUIPrefixStyle(textFields, typeStyle, defaultStyle, className)}
    ${renderMUILabelFieldStyle(textFields, typeStyle, defaultStyle, className)}
    ${areaHeight}
    ${textAreaStyle}
    ${toCSS(
      `${textFields
        .map(
          (
            v
          ) => `${className}.ft--theme--FormField--${v} input, ${className}.ft--theme--FormField--${v} [role="textbox"],
        ${className}.ft--theme--FormField--${v} input::placeholder`
        )
        .join(',')}`,
      `font-family: ${trimFont(_.get(style, 'input.fontFamily', className ? '' : ''))};
        font-size: ${_.get(style, 'input.fontSize', className ? '' : '')};
        font-weight: ${_.get(style, 'input.fontWeight', className ? '' : '')};
        color: var(--color, ${_.get(style, 'text', className ? '' : '')});
        text-align: ${_.get(style, 'input.textAlign', '')};
        ${underlineColor ? `--underline: ${_.get(style, 'input.--underline', '')};` : ''}
        ${height ? `height: ${height};box-sizing: border-box;` : ''}z-index: 1;
        ${inputPaddingStyle}
        ${_.get(style, 'root.customCSS', '')};`,
      _.get(style, 'text', className ? '' : ''),
      _.get(style, 'input.fontFamily', className ? '' : ''),
      _.get(style, 'input.fontSize', className ? '' : ''),
      _.get(style, 'input.fontWeight', className ? '' : ''),
      _.get(style, 'input.textAlign', className ? '' : ''),
      borderRadius,
      height,
      _.get(style, 'root.customCSS', ''),
      _.get(style, 'input.--underline', '')
    )}
    ${
      inputShadow
        ? toCSS(
            `${textFields.map((v) => `${className}.ft--theme--FormField--${v} input, ${className}.ft--theme--FormField--${v} [role="textbox"]`).join(',')}`,
            `${inputShadow};`,
            inputShadow
          )
        : ''
    }
    ${toCSS(
      `${textFields.map((v) => `${className}.ft--theme--FormField--${v} input::placeholder`).join(',')}`,
      `opacity: 1;${getStyleObjCss(placeholderStyles)};`,
      _.size(placeholderStyles)
    )}
    ${toCSS(
      `${textFields.map((v) => `${className}.ft--theme--FormField--${v} input::placeholder`).join(',')}`,
      `border-radius: 0px !important; padding: 0 !important;`,
      inputPaddingStyle
    )}
    ${toCSS(
      `${textFields.map((v) => `${className}.ft--theme--FormField--${v} textarea::placeholder`).join(',')}`,
      `opacity: 1;${getStyleObjCss(placeholderStyles)}`,
      _.size(placeholderStyles)
    )}
    ${toCSS(
      `${textFields.map((v) => `${className}.ft--theme--FormField--${v} .ft--theme--FormField-elRoot`).join(',')}`,
      `border-radius: ${borderRadius};`,
      borderRadius
    )}
    ${toCSS(
      `${textFields.map((v) => `${className}.ft--theme--FormField--${v}.ft--theme--FormField--filled > div`).join(',')}`,
      `background-color: ${_.get(style, 'filled', className ? '' : '')};border-radius: ${borderRadius};`,
      _.get(style, 'filled', className ? '' : ''),
      borderRadius
    )}
    ${toCSS(
      textFields
        .map(
          (v) =>
            `${className}.ft--theme--FormField--${v} > div:before, ${className}.ft--theme--FormField--${v} > div:hover:before`
        )
        .join(','),
      `border-bottom: 1px solid ${_.get(style, 'primary', '')} !important;`,
      _.get(style, 'primary', '')
    )}
    ${toCSS(
      textFields
        .map((v) => `${className}.ft--theme--FormField--${v}.fterror:not(.disableUnderlineStatus) > div:before`)
        .join(','),
      `border-bottom: 1px solid ${_.get(style, 'error', '')}!important;`,
      _.get(style, 'error', '')
    )}
    ${toCSS(
      textFields
        .map((v) => `${className}.ft--theme--FormField--${v}.ftsuccess:not(.disableUnderlineStatus) > div:before`)
        .join(','),
      `border-bottom: 1px solid ${_.get(style, 'success', '')}!important;`,
      _.get(style, 'success', '')
    )}
    ${toCSS(
      textFields.map((v) => `${className}.ft--theme--FormField--${v} > div:after`).join(','),
      `border-bottom: 2px solid ${_.get(style, 'primary', '')}!important;`,
      _.get(style, 'primary', '')
    )}
    ${toCSS(
      textFields
        .map((v) => `${className}.ft--theme--FormField--${v}.fterror:not(.disableUnderlineStatus) > div:after`)
        .join(','),
      `border-bottom: 2px solid ${_.get(style, 'error', '')}!important;`,
      _.get(style, 'error', '')
    )}
    ${toCSS(
      textFields
        .map((v) => `${className}.ft--theme--FormField--${v}.ftsuccess:not(.disableUnderlineStatus) > div:after`)
        .join(','),
      `border-bottom: 2px solid ${_.get(style, 'success', '')}!important;`,
      _.get(style, 'success', '')
    )}
    ${toCSS(
      textFields
        .map(
          (v) =>
            `${className}.ft--theme--FormField--${v} > div > .ftnotchedOutline,${className}.ft--theme--FormField--${v} > div:hover > .ftnotchedOutline`
        )
        .join(','),
      `border-color: ${_.get(style, 'primary', '')};border-radius: ${borderRadius};${renderBorderStyle(style, ['input'], '', '', true)}`,
      _.get(style, 'primary', ''),
      borderRadius,
      _.size(style?.input)
    )}
    ${toCSS(
      textFields.map((v) => `${className}.ft--theme--FormField--${v}.ft--theme--FormField--outlined input`).join(','),
      `margin-top: -2px;`,
      true
    )}

    ${toCSS(
      textFields
        .map((v) => `${className}.ft--theme--FormField--${v}.ft--theme--FormField--outlined textarea`)
        .join(','),
      `margin-top: -${!style.sizeVariant || style.sizeVariant == 'medium' ? 9 : 2}px;`,
      true
    )}
    ${toCSS(
      textFields
        .map(
          (v) =>
            `${className}.ft--theme--FormField--${v} > div > .ftnotchedOutline-classic,${className}.ft--theme--FormField--${v} > div:hover > .ftnotchedOutline-classic`
        )
        .join(','),
      `background-color: var(--bg-color, ${filled}) !important;border-radius: ${borderRadius};z-index: 0;top: -2px;`,
      filled,
      borderRadius
    )}
${toCSS(
  textFields
    .map(
      (v) => `${className}.ft--theme--FormField--${v}.fterror:not(.disableUnderlineStatus) > div > .ftnotchedOutline`
    )
    .join(','),
  ` border-color: ${_.get(style, 'error', '')}!important;`,
  _.get(style, 'error', '')
)}
${toCSS(
  textFields
    .map(
      (v) => `${className}.ft--theme--FormField--${v}.ftsuccess:not(.disableUnderlineStatus) > div > .ftnotchedOutline`
    )
    .join(','),
  `border-color: ${_.get(style, 'success', '')}!important;`,
  _.get(style, 'success', '')
)}${extra}`
}

export const getSmartFieldStyle = (styleObj, classKey, isElem = false, elemPrefix = '') => {
  let style = ''
  const className = ''
  let styleConf = mergeDeep(
    {},
    styleObj.all ?? {},
    styleObj.root ?? {},
    styleObj.messages ?? {},
    styleObj.input ?? {},
    styleObj.label ?? {},
    styleObj
  )
  _.each(classKey === 'PageSearch' ? ['text'] : THEME_FORM_FIELD_ELEMENTS_KEYS, (key) => {
    switch (key) {
      case 'signature': {
        let keys = getThemeFormFieldsKeySet(key)
        style += renderMUISignatureStyle(keys, {}, styleConf, className)
        break
      }
      case 'select':
      case 'multiSelect':
      case 'dropdown': {
        let keys = getThemeFormFieldsKeySet(key)
        style += renderMUIDropDownFieldStyle(keys, {}, styleConf, className)
        break
      }
      case 'richtextarea':
      case 'currency':
      case 'search':
      case 'lookup':
      case 'url':
      case 'date':
      case 'time':
      case 'daterange':
      case 'datetime':
      case 'text':
      case 'textarea':
      case 'email':
      case 'number':
      case 'phone': {
        let keys = getThemeFormFieldsKeySet(key)
        style += renderMUITextFieldStyle(keys, {}, styleConf, className)
        break
      }
      case 'radio':
      case 'checkbox':
      case 'switcher': {
        let keys = getThemeFormFieldsKeySet(key)
        style += renderMUICheckFieldStyle(keys, {}, styleConf, className)
        break
      }
      case 'slider': {
        let keys = getThemeFormFieldsKeySet(key)
        style += renderMUISliderFieldStyle(keys, {}, styleConf, className)
        break
      }
      case 'buttongroup': {
        let keys = getThemeFormFieldsKeySet(key)
        style += renderMUIBtnGrpFieldStyle(keys, {}, styleConf, className)
        break
      }
      case 'fileupload':
      case 'upload': {
        let keys = getThemeFormFieldsKeySet(key)
        style += renderMUIUploadFieldStyle(keys, {}, styleConf, className)
        break
      }
    }
  })
  if (isElem) {
    return style.replace(/ft--theme--FormField/g, `${elemPrefix}`)
  }
  return style.replace(/FormField/g, classKey)
}

export const makeSmartVFieldStyle = (login, reg, profile, verf) => {
  let projectStyle = ''

  const process = {
    SmartVLoginField: login.toJS(),
    SmartVRegField: reg.toJS(),
    SmartVProfileField: profile.toJS(),
    SmartVVerfField: verf.toJS(),
  }
  const className = ''

  _.each(process, (styleConfObj, classKey) => {
    let style = ''
    let styleConf = mergeDeep({}, styleConfObj.root || {}, styleConfObj)
    _.each(THEME_FORM_FIELD_ELEMENTS_KEYS, (key) => {
      switch (key) {
        case 'signature': {
          let keys = getThemeFormFieldsKeySet(key)
          style += renderMUISignatureStyle(keys, {}, styleConf, className)
          break
        }
        case 'addressFinder':
        case 'externalLookup':
        case 'select':
        case 'multiSelect':
        case 'dropdown': {
          let keys = getThemeFormFieldsKeySet(key)
          style += renderMUIDropDownFieldStyle(keys, {}, styleConf, className)
          break
        }
        case 'richtextarea':
        case 'currency':
        case 'search':
        case 'lookup':
        case 'url':
        case 'date':
        case 'time':
        case 'datetime':
        case 'text':
        case 'textarea':
        case 'email':
        case 'number':
        case 'phone': {
          let keys = getThemeFormFieldsKeySet(key)
          style += renderMUITextFieldStyle(keys, {}, styleConf, className)
          break
        }
        case 'radio':
        case 'checkbox':
        case 'switch':
        case 'switcher': {
          let keys = getThemeFormFieldsKeySet(key)
          style += renderMUICheckFieldStyle(keys, {}, styleConf, className)
          break
        }
        case 'slider': {
          let keys = getThemeFormFieldsKeySet(key)
          style += renderMUISliderFieldStyle(keys, {}, styleConf, className)
          break
        }
        case 'buttongroup': {
          let keys = getThemeFormFieldsKeySet(key)
          style += renderMUIBtnGrpFieldStyle(keys, {}, styleConf, className)
          break
        }
        case 'fileupload':
        case 'upload': {
          let keys = getThemeFormFieldsKeySet(key)
          style += renderMUIUploadFieldStyle(keys, {}, styleConf, className)
          break
        }
      }
    })
    projectStyle += style.replace(/FormField/g, classKey)
    // .replace(/\n/g, '')
  })

  // console.log('projectStyle >>>', projectStyle)

  return projectStyle
}

export const makeFormFieldStyle = (style, className = '', control = null, defaultStyles = {}) => {
  let paperClass = className
  let labelClass = className
  let dropZoneClass = className
  if (paperClass) {
    paperClass += '--paper'
    labelClass += '--label'
    dropZoneClass += '--dropzone'
  }

  if (control !== null) {
    switch (control) {
      case 'signature':
        return renderMUISignatureStyle([control], style, defaultStyles, className)
      case 'fileupload':
        return renderMUIUploadFieldStyle([control], style, {}, className)
      case 'addressFinder':
      case 'externalLookup':
      case 'select':
      case 'multiSelect':
        return renderMUIDropDownFieldStyle([control], style, defaultStyles, className)
      case 'daterange':
      case 'richtextarea':
      case 'currency':
      case 'search':
      case 'lookup':
      case 'date':
      case 'time':
      case 'datetime':
      case 'text':
      case 'textarea':
      case 'email':
      case 'url':
      case 'number':
      case 'phone':
      case 'counter':
        return renderMUITextFieldStyle([control], style, defaultStyles, className)
      case 'radio':
      case 'switch':
      case 'switcher':
      case 'checkbox':
        return renderMUICheckFieldStyle(
          [String(control).replace('switcher', 'switch')],
          style,
          defaultStyles,
          className
        )
      case 'slider':
        return renderMUISliderFieldStyle([control], style, defaultStyles, className)
      case 'buttongroup':
        return renderMUIBtnGrpFieldStyle([control], style, defaultStyles, className)
      default:
        return ''
    }
  }

  let projectStyles = ''
  _.each(THEME_FORM_FIELD_ELEMENTS_KEYS, (key) => {
    switch (key) {
      case 'signature': {
        let keys = getThemeFormFieldsKeySet(key)
        projectStyles += renderMUISignatureStyle(keys, _.get(style, key, {}), _.get(style, 'all', {}), className)
        break
      }
      case 'addressFinder':
      case 'externalLookup':
      case 'select':
      case 'multiSelect':
      case 'dropdown': {
        let keys = getThemeFormFieldsKeySet(key)
        projectStyles += renderMUIDropDownFieldStyle(keys, _.get(style, key, {}), _.get(style, 'all', {}), className)
        break
      }
      case 'richtextarea':
      case 'currency':
      case 'search':
      case 'lookup':
      case 'url':
      case 'date':
      case 'time':
      case 'datetime':
      case 'daterange':
      case 'text':
      case 'textarea':
      case 'email':
      case 'counter':
      case 'number':
      case 'phone': {
        let keys = getThemeFormFieldsKeySet(key)
        projectStyles += renderMUITextFieldStyle(keys, _.get(style, key, {}), _.get(style, 'all', {}), className)
        break
      }
      case 'radio':
      case 'checkbox':
      case 'switcher': {
        let keys = getThemeFormFieldsKeySet(key)
        projectStyles += renderMUICheckFieldStyle(keys, _.get(style, key, {}), _.get(style, 'all', {}), className)
        break
      }
      case 'slider': {
        let keys = getThemeFormFieldsKeySet(key)
        projectStyles += renderMUISliderFieldStyle(keys, _.get(style, key, {}), _.get(style, 'all', {}), className)
        break
      }
      case 'buttongroup': {
        let keys = getThemeFormFieldsKeySet(key)
        projectStyles += renderMUIBtnGrpFieldStyle(keys, _.get(style, key, {}), _.get(style, 'all', {}), className)
        break
      }
      case 'fileupload':
      case 'upload': {
        let keys = getThemeFormFieldsKeySet(key)
        if (style.upload) {
          style.upload.root = _.get(style, 'lg.root.upload', {})
        }
        projectStyles += renderMUIUploadFieldStyle(keys, _.get(style, 'upload', {}), _.get(style, 'all', {}), className)
        break
      }
    }
  })
  return projectStyles.replace(/\n/g, '')
}

export const getFormFieldElementStyles =
  (layoutMode, themeProvided = Map()) =>
  (styles, key) => {
    let style = styles.get(layoutMode, Map())
    _.each(LAYOUT_MODE.slice(0, LAYOUT_MODE.indexOf(layoutMode)), (mode) => {
      style = style.mergeDeep(styles.get(mode, Map()))
    })
    style = style.mergeDeep(styles.get(layoutMode, Map())).toJS()
    style.version = styles.get('version')
    const control = fixFormFieldStyleType(styles.get('control', 'text'))
    let theme = (isImmutable(themeProvided) ? themeProvided : Map()).mergeDeep(themeProvided.get('lg', Map()))
    let themeControl = String(control).replace('select', 'dropdown')
    if (!isImmutable(theme.get(control))) {
      theme = theme.set(control, Map())
    }
    if (themeControl === 'search') {
      theme = theme.setIn(
        ['lg', 'root'],
        theme.getIn(['lg', 'root'], Map()).mergeDeep(theme.getIn(['lg', 'root', 'search'], Map()))
      )
    }
    let rootStyle = theme
      .getIn(['lg', 'root', 'all'], Map())
      .mergeDeep(theme.getIn(['lg', 'root', control], Map()))
      .mergeDeep(theme.getIn(['lg', 'root', themeControl], Map()))
    const commonTheme = toJS(theme.get('all', Map()).mergeDeep(theme.get('lg', Map()).setIn(['root'], rootStyle)))
    const controlTheme = toJS(theme.get(control, Map()).mergeDeep(theme.getIn([themeControl], Map())))
    const defaultStyles = mergeDeep(commonTheme, controlTheme)
    if (control === 'fileupload') {
      style.fileUploadVariant = theme.getIn(['upload', 'fileUploadVariant'], 'dropzone')
    } else if (control === 'buttongroup') {
      _.set(style, ['input', 'shadow'], styles.getIn([layoutMode, 'input', 'shadow'], List()).toJS())
      _.set(style, ['inputHover', 'shadow'], styles.getIn([layoutMode, 'inputHover', 'shadow'], List()).toJS())
    }
    const additionalControls = styles
      .get('additionalControls', List())
      .filter((val) => fixFormFieldStyleType(val) !== control && val !== themeControl)
    let strStyles = String(makeFormFieldStyle(style, `.ft--${key}--root`, control, defaultStyles))
      .trim()
      .replace(/\n/g, '')
    if (List.isList(additionalControls) && additionalControls.size > 0) {
      additionalControls.map((control) => {
        control = fixFormFieldStyleType(control)
        let themeControl = String(control).replace('select', 'dropdown')
        const theme = isImmutable(themeProvided) ? themeProvided : Map()
        const commonTheme = toJS(theme.get('all', Map()).mergeDeep(theme.get('lg', Map()).setIn(['root'], rootStyle)))
        const controlTheme = toJS(theme.get(control, Map()))
        const themeControlStyle = toJS(theme.get(themeControl, Map()))
        const defaultStyles = mergeDeep({}, commonTheme, controlTheme, themeControlStyle)
        strStyles += String(makeFormFieldStyle(style, `.ft--${key}--root`, control, defaultStyles))
          .trim()
          .replace(/\n/g, '')
      })
    }
    return strStyles
  }

function RenderElementArrayStyles({ target, elements = Map() }) {
  const layoutMode = useLayoutMode()
  const defaultTheme = useSelector((state) => state.getIn(['project', 'Settings', 'theme', 'FormField'], Map()))
  const style = useMemo(
    () => String(elements.map(getFormFieldElementStyles(layoutMode, defaultTheme)).toList().join('')).trim(),
    [elements, layoutMode, defaultTheme]
  )
  return <StyleElement uid={`formField${target}`} style={style} />
}

const ProjectElementsStyles = () => {
  const projectFormElements = useSelector((state) => {
    let elements = state
      .getIn(['project', 'Settings', 'elements'], Map())
      .filter((elem) => ['FormField', 'Search', 'AddressFinder', 'ExternalLookup'].includes(elem.get('type')))
      .reduce((r, elem) => {
        const ref = elem.getIn(['props', 'styleRef'])
        const style = state
          .getIn(['project', 'Settings', 'styles', ref], Map())
          .set(
            'control',
            elem.get('type') === 'Search'
              ? 'search'
              : ['AddressFinder', 'ExternalLookup'].indexOf(elem.get('type')) !== -1
                ? _.camelCase(elem.get('type'))
                : elem.getIn(['props', 'control'], 'text')
          )
        if (r.has(ref)) {
          return r.setIn(
            [ref, 'additionalControls'],
            r
              .getIn([ref, 'additionalControls'], List())
              .push(elem.get('type') === 'Search' ? 'search' : elem.getIn(['props', 'control'], 'text'))
          )
        } else {
          return r.set(ref, style)
        }
      }, Map())
    let global = state
      .getIn(['project', 'Settings', 'styles'], Map())
      .filter((style) => style.get('isGlobal'))
      .reduce((r, style, key) => {
        let isProject = state.hasIn(['project', 'Settings', 'elements', key])
        let elem = state
          .getIn([...(isProject ? ['project', 'Settings'] : ['pages', state.get('page'), 'Props']), 'elements'], Map())
          .find((elem) => elem.getIn(['props', 'styleRef']) === key)
        if (elem) {
          return r.set(
            key,
            style.set(
              'control',
              elem.get('type') === 'Search'
                ? 'search'
                : ['AddressFinder', 'ExternalLookup'].indexOf(elem.get('type')) !== -1
                  ? _.camelCase(elem.get('type'))
                  : elem.getIn(['props', 'control'], 'text')
            )
          )
        } else {
          return r.set(key, style)
        }
      }, Map())
    return Map().merge(elements, global)
  }, isSame)

  return <RenderElementArrayStyles target="project" elements={projectFormElements} />
}

const PageElementsStyles = () => {
  const pageFormElements = useSelector(
    (state) =>
      state
        .getIn(['pages', state.get('page'), 'Props', 'elements'], Map())
        .filter((elem) => ['FormField', 'Search', 'AddressFinder', 'ExternalLookup'].includes(elem.get('type')))
        .reduce((r, elem) => {
          const ref = elem.getIn(['props', 'styleRef'])
          if (state.getIn(['pages', state.get('page'), 'Props', 'styles', ref], Map()).size > 0) {
            const style = state
              .getIn(['pages', state.get('page'), 'Props', 'styles', ref], Map())
              .set(
                'control',
                elem.get('type') === 'Search'
                  ? 'search'
                  : ['AddressFinder', 'ExternalLookup'].indexOf(elem.get('type')) !== -1
                    ? _.camelCase(elem.get('type'))
                    : elem.getIn(['props', 'control'], 'text')
              )
            if (!state.getIn(['project', 'Settings', 'styles', ref, 'isGlobal'])) {
              if (r.has(ref)) {
                return r.setIn(
                  [ref, 'additionalControls'],
                  r
                    .getIn([ref, 'additionalControls'], List())
                    .push(
                      elem.get('type') === 'Search'
                        ? 'search'
                        : ['AddressFinder', 'ExternalLookup'].indexOf(elem.get('type')) !== -1
                          ? _.camelCase(elem.get('type'))
                          : elem.getIn(['props', 'control'], 'text')
                    )
                )
              } else {
                return r.set(ref, style)
              }
            } else {
              return r
            }
          } else {
            return r
          }
        }, Map()),
    isSame
  )

  return <RenderElementArrayStyles target="page" elements={pageFormElements} />
}

export const FormFieldElementsDimensions = () => {
  const mode = useLayoutMode()
  const elements = useSelector((state) => {
    let elements = Map()
    if ([SURVEY_TYPE, SURVEY_EMAIL_TYPE].includes(state.getIn(['project', 'ProjectType']))) {
      return elements
    }
    state.getIn(['project', 'Settings', 'elements'], Map()).map((elem, id) => {
      if (
        Map.isMap(elem) &&
        elem.get('type') === 'FormField' &&
        elem.getIn(['props', mode, 'position', 'disableInherit'])
      ) {
        elements = elements.set(id, elem)
      }
    })
    state.getIn(['pages', state.get('page'), 'Props', 'elements'], Map()).map((elem, id) => {
      if (
        Map.isMap(elem) &&
        elem.get('type') === 'FormField' &&
        elem.getIn(['props', mode, 'position', 'disableInherit'])
      ) {
        elements = elements.set(id, elem)
      }
    })
    return elements
  }, isSame)

  const style = useMemo(() => {
    let text = []

    elements.map((elem, id) => {
      const position = elem.getIn(['props', mode, 'position'], Map())
      const sizes = _.reduce(
        ALL_SIZE_KEYS,
        (r, key) => {
          return {
            ...r,
            [key]: position.get(key, ''),
            [`${key}Unit`]: position.get(key, 'px'),
          }
        },
        {}
      )
      const sizeStyles = getStyle(sizes)
      if (sizeStyles.width && !sizeStyles.maxWidth) {
        sizeStyles.maxWidth = sizeStyles.width
      }
      if (sizeStyles.height && !sizeStyles.minHeight) {
        sizeStyles.minHeight = sizeStyles.height
      }

      if (
        Map.isMap(elem) &&
        elem.get('type') === 'FormField' &&
        elem.getIn(['props', mode, 'position', 'disableInherit'])
      ) {
        if (!sizeStyles.width) {
          sizeStyles.width = 'auto'
        }
        if (!sizeStyles.height) {
          sizeStyles.height = 'auto'
        }
      }
      let style = `.ft--pos--${id},.ft--pos--${id} .ft--theme--FormField-container,.ft--pos--${id} .ft--FormField--dropzone,.ft--pos--${id}.ft--theme--FormField--${elem.getIn(['props', 'control'])}.ft--theme--FormField-container{height: auto;min-height: auto;`
      const width = _.pick(sizeStyles, ['width', 'minWidth', 'maxWidth'])
      if (_.size(width) === 0) {
        style += `height: auto;`
      }
      _.each(width, (v, k) => {
        style += `${kebabCase(k)}: ${v};`
      })
      style += `}.ft--pos--${id} .ft--control--root ,.ft--pos--${id} > .ft--control--root > .ft--theme--FormField-container .ft--theme--FormField-elRoot{min-height: auto;height: auto;}`
      style += `.ft--pos--${id} .ft--theme--FormField-container [role="textbox"],.ft--pos--${id} .ft--theme--FormField-container textarea,.ft--pos--${id} .ft--theme--FormField-container input,.ft--pos--${id} .ft--theme--FormField-elRoot,.ft--pos--${id} .ft--theme--FormField--buttongroup button,.ft--pos--${id} .ft--theme--FormField--canvas,.ft--pos--${id} .ft--FormField--dropzone{`
      _.each(_.pick(sizeStyles, ['height', 'minHeight', 'maxHeight']), (v, k) => {
        style += `${kebabCase(k)}: ${v};`
      })
      style += `}.ft--pos--${id} > .ft--control--root > .ft--theme--FormField-container,.ft--pos--${id}.ft--theme--FormField--${elem.getIn(['props', 'control'])}.ft--theme--FormField-container{`
      _.each(sizeStyles, (v, k) => {
        style += `${kebabCase(k)}: ${v};`
      })
      style += 'min-height: auto;height: auto;min-width: auto;}'
      if (elem.getIn(['props', 'control']) === 'fileupload') {
        style += `.ft--pos--${id} .ft--FormField--dropzone{min-width: auto;min-height: auto;}`
        style += `.ft--pos--${id}.ft--theme--FormField--fileupload.ft--theme--FormField-container, .ft--pos--${id} .ft--control--root > .ft--theme--FormField-container{min-height: auto;min-width: auto;`
        _.each(sizeStyles, (v, k) => {
          style += `${kebabCase(k)}: ${v};`
        })
        style += '}'
      }
      text.push(style)
    })

    return text.join('')
  }, [mode, elements])
  return <StyleElement uid="ff-elem-custom-size" style={style} />
}

export function ElementFormFieldStyle() {
  return (
    <>
      <ProjectElementsStyles />
      <PageElementsStyles />
    </>
  )
}

export const fixFormFieldStyleType = (str) => str.replace(/^switch$/, 'switcher')

export function ProjectFormFieldStyle() {
  const IsRenderFormField = useSelector((state) => {
    const types = [
      ...state
        .getIn(['pages', state.getIn(['page']), 'Props', 'elements'], Map())
        .map((el) => {
          if (el.get('type') == 'Widget') {
            return el.getIn(['props', 'data', 'Type'], '')
          } else {
            if (errorsFieldsControls.includes(el.get('type'))) {
              return 'FormField'
            } else {
              return el.get('type')
            }
          }
        })
        .toList()
        .toJS(),
      ...state
        .getIn(['project', 'Settings', 'elements'], Map())
        .map((el) => el.get('type'))
        .toList()
        .toJS(),
    ]
    return ['FormField', 'AddressFinder', 'ExternalLookup', 'ImageChoice'].some((v) => types.includes(v))
  })
  const accessabilityMode = useSelector((state) => {
    let sett = state.getIn(['project', 'Settings', 'accessabilityToolbar'], Map())
    if (sett.get('enabled') && sett.get('isAccessibilityMode')) {
      return true
    }
    return false
  })
  const useSmartV = useSelector((state) => state.getIn(['project', 'isPublic'], true) === false)
  const formField = useSelector((state) => {
    let theme = state.getIn(['project', 'Settings', 'theme', 'FormField'], Map())
    if (accessabilityMode) {
      return theme.setIn(['upload', 'fileUploadVariant'], 'button')
    }
    return theme
  })
  const SmartVLoginFields = useSelector(
    (state) =>
      state
        .getIn(['project', 'Settings', 'theme', 'lg', 'SmartVLoginField'], Map())
        .mergeDeep(state.getIn(['project', 'Settings', 'styles', 'SmartVLoginField', 'lg'], Map())),
    isSame
  )
  const SmartVRegFields = useSelector((state) =>
    state
      .getIn(['project', 'Settings', 'theme', 'lg', 'SmartVRegField'], Map())
      .mergeDeep(state.getIn(['project', 'Settings', 'styles', 'SmartVRegField', 'lg'], Map()))
  )
  const SmartVProfileField = useSelector(
    (state) =>
      state
        .getIn(['project', 'Settings', 'theme', 'lg', 'SmartVProfileField'], Map())
        .mergeDeep(state.getIn(['project', 'Settings', 'styles', 'SmartVProfileField', 'lg'], Map())),
    isSame
  )
  const SmartVVerfField = useSelector(
    (state) =>
      state
        .getIn(['project', 'Settings', 'theme', 'lg', 'SmartVVerfField'], Map())
        .mergeDeep(state.getIn(['project', 'Settings', 'styles', 'SmartVVerfField', 'lg'], Map())),
    isSame
  )

  const [style, setStyle] = useState(() =>
    [
      IsRenderFormField ? makeFormFieldStyle(formField.toJS()) : '',
      useSmartV ? makeSmartVFieldStyle(SmartVLoginFields, SmartVRegFields, SmartVProfileField, SmartVVerfField) : '',
    ].join('')
  )

  useEffect(() => {
    const renderStyles = async () => {
      if (typeof window !== 'undefined' && typeof window?.__FT__BUILDER_API__?.workerTask === 'function') {
        let styles = []
        if (IsRenderFormField) {
          let formFieldStyle = await window.__FT__BUILDER_API__.workerTask('FormField', formField.toJS())
          if (formFieldStyle) {
            styles.push(formFieldStyle)
          } else {
            styles.push(makeFormFieldStyle(formField.toJS()))
          }
        }
        if (useSmartV) {
          // let smartVStyle = await window.__FT__BUILDER_API__.workerTask('SmartV', SmartVLoginFields.toJS(), SmartVRegFields.toJS(), SmartVProfileField.toJS(), SmartVVerfField.toJS())
          // if (smartVStyle) {
          //     styles.push(smartVStyle)
          // } else {
          styles.push(makeSmartVFieldStyle(SmartVLoginFields, SmartVRegFields, SmartVProfileField, SmartVVerfField))
          // }
        }
        return styles.join('')
      } else {
        return [
          IsRenderFormField ? makeFormFieldStyle(formField.toJS()) : '',
          useSmartV
            ? makeSmartVFieldStyle(SmartVLoginFields, SmartVRegFields, SmartVProfileField, SmartVVerfField)
            : '',
        ].join('')
      }
    }
    renderStyles().then(setStyle)
  }, [formField, IsRenderFormField, SmartVLoginFields, SmartVRegFields, SmartVProfileField, useSmartV, SmartVVerfField])

  return <StyleElement uid="smartv-form-field" style={style} />
}
